const Highcharts = require("highcharts");

if (LUX) {
    LUX.mark("chartsjs before");
}

window.isDev = function () {
    return (
        /^(local|dev|staging)(\..+)?\.speedcurve\.com$/.test(window.location.host) ||
        /\.herokuapp\.com$/.test(window.location.host)
    );
};

function reportFunctionUsage(functionName) {
    if (window.Sentry && window.location.hostname === "staging.speedcurve.com") {
        window.Sentry.captureMessage(`Legacy function in charts.js was called: ${functionName}`);
    }
}

if ("number" === typeof window.timezoneOffset) {
    Highcharts.setOptions({ global: { useUTC: true, timezoneOffset: window.timezoneOffset } });
} else {
    Highcharts.setOptions({ global: { useUTC: false } });
}

const noDataMessage = "No data to display. Try changing the filters.";

Highcharts.setOptions({
    lang: {
        noData: noDataMessage,
    },
});

// Wrap Highchart's colorizeItem function to provide a "legendColor" option. This
// option is used when we display legends on a chart with a budget. We achieve
// the red "over budget" effect by setting the series color to red and setting
// the "negative color" to the original color. However, this causes chart legends
// to be red.
//
// This wrapper sets the item color to the value of the "legendColor" option,
// and then restores the orignial item color.
Highcharts.wrap(Highcharts.Legend.prototype, "colorizeItem", function (proceed, item) {
    var originalColor = item.color;

    if (item.options.legendColor) {
        item.color = item.options.legendColor;
    }

    proceed.apply(this, Array.prototype.slice.call(arguments, 1));

    item.color = originalColor;
});

// fetch() some JSON with same-origin credentials and default error handling
window.fetchJSON = function (url, fetchOptions) {
    fetchOptions = fetchOptions || {};

    // Attempt to fix when URLs fetched from a shared dashboard return 401 Not Authorized
    if (window.beingShared && window.share) {
        url += (url.indexOf("?") === -1 ? "?" : "&") + "share=" + window.share;
    }

    return new Promise(function (resolve, reject) {
        var doReject = function (e, responseBody) {
            if (responseBody) {
                e.message =
                    "fetchJSON() could not decode the response from " +
                    url.slice(0, 150) +
                    ": " +
                    responseBody.slice(0, 150);
            }

            if (e.response && e.response.redirected) {
                e.message += " (redirected: true)";
            }

            if (window.beingShared) {
                e.message += " (beingShared: true)";
            }

            if (e.name !== "AbortError") {
                console.error(e);
            }

            reject(e);
        };

        fetch(url, {
            credentials: "same-origin",
            headers: {
                accept: "application/json",
            },
            ...fetchOptions,
        })
            .then(function (res) {
                if (res.status === 200) {
                    res.text().then(function (body) {
                        try {
                            resolve(JSON.parse(body));
                        } catch (e) {
                            e.response = res;
                            doReject(e, body);
                        }
                    });
                } else {
                    reject(new Error(`Error response from server: ${res.status} ${res.statusText}`));
                }
            })
            .catch(doReject);
    });
};

// Add an empty chart wrapper to reserve space in the page.
window.addPlaceholder = function (parentId, title, containerId, bHideNotes, bHide, height, optionsHash) {
    reportFunctionUsage("addPlaceholder");
    if (!optionsHash) {
        optionsHash = {};
    }
    var chartWrapper = getChartWrapper(parentId, containerId);
    if (bHide) {
        chartWrapper.style.display = "none";
    }
    if (!optionsHash.chartOnly) {
        window.addHeader(chartWrapper, title, null, bHideNotes);
    }
    // create div for content
    var chartContent = document.createElement("div");
    chartContent.className = "chart__content";
    chartWrapper.appendChild(chartContent);

    var space = document.createElement("div");
    space.id = containerId;
    if (height === undefined) {
        space.style.height = "510px";
    } else {
        space.style.height = height + "px";
    }
    chartContent.appendChild(space);
};

function getChartTimestamps(allSeries) {
    reportFunctionUsage("getChartTimestamps");
    const seriesWithData = allSeries.filter((series) => "undefined" !== typeof series.data && series.data.length > 0);
    const start = seriesWithData.reduce((prev, series) => Math.min.apply(null, [prev, series.data[0].x]), Infinity);
    const end = seriesWithData.reduce(
        (prev, series) => Math.max.apply(null, [prev, series.data[series.data.length - 1].x]),
        0,
    );
    return {
        start,
        end,
        middle: Math.round(end - (end - start) / 2),
    };
}

// Add the stats and chart container.
//
// optionsHash was introduced to manage new options without adding more function parameters. Available options are:
//     overBudgetSummary (bool | default: false) - Add a summary below the chart of metrics that are over their budget
window.addMarkup = function (
    parentId,
    title,
    containerId,
    seriesData,
    dataAbbrev,
    height,
    rank,
    bHideNotes,
    showEdit,
    optionsHash,
) {
    reportFunctionUsage("addMarkup");
    if (!optionsHash) {
        optionsHash = {};
    }

    var chartWrapper = getChartWrapper(parentId, containerId);

    // clear current chart stuff
    chartWrapper.innerHTML = "";
    chartWrapper.style.background = "";
    chartWrapper.style.opacity = "1.0";
    chartWrapper.style.display = "block"; // it is possible that it was initially hidden
    if (!optionsHash.chartOnly) {
        window.addHeader(chartWrapper, title, seriesData, bHideNotes, showEdit, optionsHash);
    }

    const chartFilters = document.createElement("div");
    chartFilters.className = "chart__filters";
    chartWrapper.appendChild(chartFilters);

    // create div for content
    var chartContent = document.createElement("div");
    chartContent.className = "chart__content";
    chartWrapper.appendChild(chartContent);

    let compare = false;
    if (
        !optionsHash.disableCompare &&
        typeof window.dropdown !== "undefined" &&
        typeof window.dropdown.dc.id !== "undefined"
    ) {
        if (window.dropdown.dc.id === 1) {
            compare = true;
            optionsHash.legendReverse = true;
        }
    }

    let addStats = true;

    if (optionsHash.chartOnly && seriesData && 0 < seriesData.length) {
        addStats = false;
    }

    if (seriesData && 0 < seriesData.length && seriesData[0].metrictype === "list") {
        addStats = false;
    }

    if (addStats) {
        // draw stats
        var stats = document.createElement("div");
        stats.className = "stats";

        // don't display compare if no data
        if (compare && typeof seriesData === "object" && Object.keys(seriesData).length > 0) {
            // check which data series is the longest and use that one for dates
            const timestamps = getChartTimestamps(seriesData);
            let previous_start = new Date(timestamps.start);
            let previous_end = new Date(timestamps.middle);
            let latest_start = new Date(timestamps.middle);
            let latest_end = new Date(timestamps.end);
            if (window.dropdown.dcs.id > 1) {
                previous_start = new Date(window.dropdown.dcs.id * 1000);
                previous_end = new Date(window.dropdown.dce.id * 1000);
                latest_start = new Date(window.dropdown.ds.id * 1000);
                latest_end = new Date(window.dropdown.de.id * 1000);
            }
            const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
            stats.className = "stats stats--compare";
            stats.innerHTML +=
                "<div class='chart__stat--compare'>" +
                "<div class='chart__stat-metric'>Previous <br>" +
                months[previous_start.getMonth()] +
                " " +
                previous_start.getDate() +
                " - " +
                months[previous_end.getMonth()] +
                " " +
                previous_end.getDate() +
                "</div>" +
                "<div class='chart__stat-metric'>Change</div>" +
                "<div class='chart__stat-metric'>Latest <br>" +
                months[latest_start.getMonth()] +
                " " +
                latest_start.getDate() +
                " - " +
                months[latest_end.getMonth()] +
                " " +
                latest_end.getDate() +
                "</div>" +
                "</div>";
        }

        for (let i = 0; i < seriesData.length; i++) {
            const series = seriesData[i];
            const suffix = series.dataType === "correlation" ? series.suffix : dataAbbrev;

            stats.innerHTML += statsMarkup(seriesData, seriesData[i], suffix, rank, title, optionsHash);
        }
        chartContent.appendChild(stats);
    }

    var chartContainer = document.createElement("div");
    chartContainer.className = "chartContainer";
    if (height === undefined) {
        height = 400;
    }
    chartContainer.innerHTML = "<div id='" + containerId + "' style='height:" + height + "px;'></div>";
    chartContent.appendChild(chartContainer);

    if (!optionsHash.chartOnly && optionsHash.overBudgetSummary) {
        addOverBudgetSummary(chartContent, seriesData);
    }
};

// Return the wrapper for a given chart ID.
function getChartWrapper(parentId, containerId) {
    reportFunctionUsage("getChartWrapper");
    var wrapperId = containerId + "_wrapper";
    var chartWrapper = document.getElementById(wrapperId);
    if (!chartWrapper) {
        // Create a wrapper (DIV) that contains ALL of the DOM for a chart (including header etc.).
        chartWrapper = document.createElement("div");
        chartWrapper.className = "chartwrapper";
        chartWrapper.id = wrapperId;
        chartWrapper.setAttribute("elementtiming", containerId + "_chart"); // for Hero Times
        chartWrapper.setAttribute("data-container_id", containerId);
        var parent = document.getElementById(parentId) || document.body;
        parent.appendChild(chartWrapper);
    }

    return chartWrapper;
}

// Add the chart header.
window.addHeader = function (parent, title, seriesData, bHideNotes, showEdit, optionsHash) {
    reportFunctionUsage("addHeader");
    if (!optionsHash) {
        optionsHash = {};
    }

    const { hideDownload = false } = optionsHash;
    const showDownload = !hideDownload;

    // check if we should show the options menu
    const showOptions = showEdit || showDownload;

    var header = document.createElement("div");
    header.className = "chart__header";
    var titleNode = document.createElement("div");
    titleNode.className = "chart__header-title";
    titleNode.textContent = title || "";

    header.innerHTML =
        titleNode.outerHTML +
        "<div class='chart__dropdown dropdown'> " +
        (showEdit
            ? "<span class='chart__options chart__edit chart__dropdown-edit'><i class='icon-pencil' title='Edit'></i></span>"
            : "") +
        "<span class='chart__options chart__options--last' data-toggle='dropdown'>" +
        (showOptions === false
            ? ""
            : "<i class='icon-cog' title='Options'></i></span>" +
              "<ul class='chart_dropdown-menu dropdown-menu dropdown-menu-right inverse-dropdown' >" +
              (showEdit ? "<li><a href='' class='chart__dropdown-edit'>Edit Chart</a></li>" : "") +
              (showEdit ? "<li><a href='' class='chart__dropdown-copy'>Copy Chart</a></li>" : "") +
              (showEdit ? "<li><a href='' class='chart__dropdown-delete'>Delete Chart</a></li>" : "") +
              (showEdit ? "<li role='separator' class='divider'></li>" : "") +
              (showEdit ? "<li><a href='' class='chart__move-top'>Move to Top</a></li>" : "") +
              (showEdit ? "<li><a href='' class='chart__move-bottom'>Move to Bottom</a></li>" : "") +
              (showEdit ? "<li><a href='' class='chart__move-up'>Move Up</a></li>" : "") +
              (showEdit ? "<li><a href='' class='chart__move-down'>Move Down</a></li>" : "") +
              (showEdit && showDownload ? "<li role='separator' class='divider'></li>" : "") +
              (showDownload ? "<li><a href='' class='chart__dropdown-csv'>Download CSV</a></li>" : "") +
              (showDownload ? "<li><a href='' class='chart__dropdown-image'>Download Image</a></li>" : "") +
              "</ul>") +
        "</div>";

    // When the "noAutoAppend" option is true, don't automatically append the
    // header to the parent element.
    if (!optionsHash.noAutoAppend) {
        parent.appendChild(header);
    }

    // create chart description
    if (optionsHash.description) {
        var desc = document.createElement("div");
        desc.className = "chart__desc";
        desc.innerHTML = optionsHash.description;
        parent.appendChild(desc);
    }

    return header;
};

function addOverBudgetSummary(parent, seriesData) {
    reportFunctionUsage("addOverBudgetSummary");
    var summary = document.createElement("div");

    var overBudgetSeries = seriesData.filter(function (series) {
        return series.budgetCrossingInfo && series.budgetCrossingInfo.type === "over";
    });

    var budgetSummaries = overBudgetSeries.map(function (series) {
        var crossing = series.budgetCrossingInfo;
        var latestValue = crossing.series.data.pop().y;

        return [
            '<div class="feed-item alert-over">',
            '<div style="padding: 5px 0;">',
            "<strong>",
            series.name,
            ' <span style="font-size: 130%; vertical-align: bottom; color: ' + series.color + ';">&mdash;</span>',
            "</strong>",
            " is currently ",
            "<strong>" + latestValue + series.suffix + "</strong>",
            " which is " + Math.round(crossing.difference_from_threshold * 100) + "% over budget",
            "</div>",
            "</div>",
        ].join("");
    });

    summary.className = "budget-summary";
    summary.innerHTML = "<div>" + budgetSummaries.join("") + "</div>";
    parent.appendChild(summary);

    return summary;
}

function statsMarkup(seriesData, series, dataLabel, showrank, title, optionsHash) {
    reportFunctionUsage("statsMarkup");
    var seriesName = series.shortName || series.name;
    var reverse = false;
    if (title.search(/pagespeed/i) > 1) {
        reverse = true;
    }
    if (title.search(/lighthouse/i) >= 0) {
        reverse = true;
    }

    // check dataLabel and try getting it from the series
    if ("undefined" === typeof dataLabel) {
        if ("undefined" !== typeof series.suffix) {
            dataLabel = series.suffix;
        } else {
            dataLabel = "";
        }
    }

    let compare = false;
    if (
        !optionsHash.disableCompare &&
        typeof window.dropdown !== "undefined" &&
        typeof window.dropdown.dc.id !== "undefined"
    ) {
        if (window.dropdown.dc.id === 1) {
            compare = true;
        }
    }

    if (compare) {
        // split and compare the time period medians
        // get start, end & middle timestamps for time range
        if ("undefined" !== typeof series.data && "undefined" !== typeof series.data[0]) {
            let stat_a;
            let stat_b;
            if (series.isCompareSeries) {
                stat_a = getSummaryStat(series);
                series.dashStyle = "ShortDash";

                // find matching latest stat
                const originalSeries = seriesData.find((s) => s.name === series.name && !s.isCompareSeries);

                if (originalSeries) {
                    stat_b = getSummaryStat(originalSeries);
                    originalSeries.color = series.color;
                    originalSeries.name = `Latest ${originalSeries.name}`;
                    series.name = `Previous ${series.name}`;
                }
            } else {
                // skip rendering stats for latest
                return "";
            }

            // work out percentage difference for two medians
            var percent_change = "-";
            if (stat_a !== 0) {
                percent_change = Math.round(((stat_b - stat_a) / stat_a) * 100);
            }
            // check which way around good/bad is. eg. lighthouse scores
            var metric_reverse = !!series.budget_reverse;
            var percent_change_color_class = "nochange";
            var percent_change_icon = "";
            if (0 === series.name.indexOf("Okay")) {
                percent_change_color_class = "nochange";
            } else if (percent_change > 0) {
                percent_change_color_class = "bad";
                if (metric_reverse) {
                    percent_change_color_class = "good";
                }
                percent_change_icon = "<i class='icon-up'></i>";
            } else if (percent_change < 0) {
                percent_change = percent_change * -1;
                percent_change_color_class = "good";
                if (metric_reverse) {
                    percent_change_color_class = "bad";
                }
                percent_change_icon = "<i class='icon-down'></i>";
            }
            if (isNaN(percent_change) || percent_change === Infinity || percent_change === undefined) {
                percent_change = "-";
            }
            if (stat_a === 0 && stat_b === 0) {
                percent_change = 0;
            }
            if (percent_change !== "-") {
                percent_change += "%";
            }
            if (stat_a === undefined || stat_a === null) {
                stat_a = "-";
            } else {
                stat_a += dataLabel;
            }
            if (stat_b === undefined || stat_b === null) {
                stat_b = "-";
            } else {
                stat_b += dataLabel;
            }

            var compare_html =
                "<div class='chart__stat--compare' style='color:" +
                ("undefined" === typeof series.color ? "#FFF" : series.color) +
                ";'>" +
                "<div class='chart__stat-metric'>" +
                seriesName +
                "<div class='chart__stat-number--compare'>" +
                stat_a +
                "</div>" +
                "</div>" +
                "<div class='chart__stat-metric'>&nbsp;" +
                "<div class='chart__stat-number--compare " +
                percent_change_color_class +
                "'>" +
                percent_change_icon +
                percent_change +
                "</div>" +
                "</div>" +
                "<div class='chart__stat-metric'>" +
                seriesName +
                "<div class='chart__stat-number--compare'>" +
                stat_b +
                "</div>" +
                "</div>" +
                "</div>";
            return compare_html;
        }
    } else if (series && ("number" === typeof series.stat || "string" === typeof series.stat)) {
        // The series contains a single (median) stat value.
        let stat = getSummaryStat(series);
        let rank = "";
        if (showrank) {
            rank = getRank(seriesData, stat, dataLabel, "number", reverse);
        }

        // build the tooltip
        var metric = "Median"; // default for most metrics
        //Now catch the exceptions
        if (series.statid) {
            switch (series.statid) {
                case "average":
                    metric = "Average";
                    break;
                case 95:
                    metric = "95th percentile";
                    break;
                case 75:
                    metric = "75th percentile";
                    break;
                default:
                    metric = "Median";
            }
        }
        var daysDropdownParam = "d";
        var dateStartDropdownParam = "ds";
        var dateEndDropdownParam = "de";

        if (window.dropdown.dashboard && window.dropdown.dashboard.indexOf("lux") === 0) {
            daysDropdownParam = "ld";
            dateStartDropdownParam = "lds";
            dateEndDropdownParam = "lde";
        }

        var overTheLastDays;

        if (window.dropdown[daysDropdownParam].id === -1) {
            overTheLastDays =
                " between " +
                window.dropdown[dateStartDropdownParam].name +
                "&mdash;" +
                window.dropdown[dateEndDropdownParam].name;
        } else {
            var daysName = window.dropdown[daysDropdownParam].name.toLowerCase();

            if (daysName === "last year") {
                overTheLastDays = " over the last year";
            } else {
                overTheLastDays = " over the last " + daysName;
            }
        }

        if (window.summaryMetric === "latest") {
            overTheLastDays = "";
        }

        // check if KB should be turned to MB
        if (dataLabel === "KB") {
            if (stat > 1024) {
                stat = stat / 1024;
                stat = stat.toFixed(2);
                dataLabel = "MB";
            }
        }

        if (dataLabel === "ms") {
            stat = window.formatNumber(stat);
        }

        if (["count", "sum"].includes(series.metrictype)) {
            stat = window.formatNumber(stat);
            metric = "Total";
        }

        let summaryType = window.summaryMetric.charAt(0).toUpperCase() + window.summaryMetric.slice(1);
        if (series.dataType === "distribution") {
            summaryType = "Aggregate";
        }

        let metricTooltip =
            "<span class='inline-help-content__tool-tip-text'><b>" +
            summaryType +
            " " +
            seriesName +
            " (" +
            metric +
            ")" +
            overTheLastDays +
            "</b>";
        if (typeof series.definition !== "undefined" && series.definition !== "") {
            metricTooltip += "<br>" + series.definition;
        }
        metricTooltip += "</span>";

        return (
            "<div class='stat' style='color: " +
            ("undefined" === typeof series.color ? "var(--high-emphasis)" : series.color) +
            ";'>" +
            "<div class='inline-help-content'>" +
            metricTooltip +
            seriesName +
            "<div class='number'>" +
            stat +
            dataLabel +
            "</div>" +
            rank +
            "</div>" +
            "</div>"
        );
    } else if (series && "object" === typeof series.stat) {
        // The series contains a single (median) stat value along with an extra description for that stat.
        let rank;
        var desc = "";
        var stat_stat = "";
        var stat_desc = "";
        if ("undefined" !== typeof series.stat && null !== series.stat) {
            if ("undefined" !== typeof series.stat.stat) {
                stat_stat = series.stat.stat;
            }
            if ("undefined" !== typeof series.stat.desc) {
                stat_desc = series.stat.desc;
            }
        }
        if (stat_stat === "") {
            dataLabel = "";
        }
        if (showrank) {
            rank = getRank(seriesData, stat_stat, dataLabel, "object", reverse);
            desc = rank + "<br>";
        }
        desc += stat_desc;
        if (dataLabel === "KB") {
            if (stat_stat > 1024) {
                stat_stat = stat_stat / 1024;
                stat_stat = stat_stat.toFixed(2);
                dataLabel = "MB";
            }
        }
        return (
            "<div class='stat' style='color: " +
            ("undefined" === typeof series.color ? "var(--high-emphasis)" : series.color) +
            ";'>" +
            seriesName +
            "<div class='number'>" +
            stat_stat +
            dataLabel +
            "</div>" +
            desc +
            "</div>\n"
        );
    } else if (series && "undefined" !== typeof series.stats) {
        // The series contains an array of stats. This typically is for a chart that only has ONE series.
        var sHtml = "";
        for (let i = 0; i < series.stats.length; i++) {
            var stat = series.stats[i];
            sHtml +=
                "<div class='stat' style='color: " +
                ("undefined" === typeof stat.color ? "var(--high-emphasis)" : stat.color) +
                ";'>" +
                stat.label +
                "<div class='number'>" +
                stat.value +
                "</div></div>\n";
        }
        return sHtml;
    }

    return "";
}

function getSummaryStat(series) {
    reportFunctionUsage("getSummaryStat");
    if (series.metrictype === "count" || series.metrictype === "sum") {
        return getSeriesTotal(series.data);
    }
    if (series.dataType === "distribution") {
        return series.stat;
    }
    switch (window.summaryMetric) {
        case "latest":
            return getSeriesLatest(series.data);
        case "maximum":
            return getSeriesMax(series.data);
        case "minimum":
            return getSeriesMin(series.data);
        default:
            return series.stat;
    }
}

function getRank(seriesData, stat, dataLabel, type, reverse) {
    reportFunctionUsage("getRank");
    var fastest;
    var rank = [];
    var position = "";
    var percent = "";
    var percent_display = "";
    // build array of valid metric values
    for (let i = 0; i < seriesData.length; i++) {
        if (typeof seriesData[i].stat !== "undefined") {
            if (type === "number") {
                rank.push(getSummaryStat(seriesData[i]));
            } else {
                if (null !== seriesData[i].stat) {
                    if ("undefined" !== typeof seriesData[i].stat.stat) {
                        rank.push(seriesData[i].stat.stat);
                    }
                }
            }
        }
    }
    // sort rank
    rank.sort(function (a, b) {
        return a - b;
    });
    if (reverse) {
        rank.reverse();
    }
    fastest = rank[0];
    // find position of stat in rank
    for (let i = 0; i < rank.length; i++) {
        if (String(rank[i]) === String(stat)) {
            position = i + 1;
            break;
        }
    }
    //get percentage diff from fastest
    if (fastest > 0) {
        percent = Math.round(((stat - fastest) / fastest) * 100);
    }
    // check if we're slower or larger
    var mode = "larger";
    if (dataLabel === "s") {
        mode = "slower";
    } else if (!dataLabel) {
        mode = "more";
        if (reverse) {
            mode = "less";
        }
    }
    if (percent !== 0 && percent !== "") {
        percent_display = percent + "% " + mode;
    }
    return "#" + position + " " + percent_display;
}

// Detects the chart type and calls the relevant *ChartMarkup() function
//
// Required config properties:
//  - containerId (string)
//  - dataAbbrev (string)
//  - dataLabel (string)
//  - parentId (string)
//  - seriesData (array)
//  - title (string)
//  - xAxisUnits (string)
//
// Optional config properties:
//  - stacking
//  - fillOpacity
//  - notes
//  - legend
//  - height (number | default: 400)
//  - rank
//  - hideNotes
//  - showEdit
//
window.chartMarkup = function (userConfig) {
    reportFunctionUsage("chartMarkup");
    var config = $.extend({}, userConfig);

    var distributionSeries = $.grep(config.seriesData, function (series) {
        return series.dataType === "distribution";
    })[0];

    if (distributionSeries) {
        // When we plot distributions, we take some of the chart labelling from
        // the series, unless it's been explicitly supplied in the chart config
        ["dataLabel", "dataAbbrev"].forEach(function (property) {
            config[property] = distributionSeries[property];
        });

        return window.distributionChartMarkup(config);
    }

    return window.lineChartMarkup(
        config.parentId,
        config.title,
        config.containerId,
        config.seriesData,
        config.dataLabel,
        config.dataAbbrev,
        config.stacking,
        config.fillOpacity,
        config.notes,
        config.legend,
        config.height,
        config.rank,
        config.hideNotes,
        config.showEdit,
        config, // Pass through the whole config object as optionsHash
    );
};

// One or more lines without any fill, where one series is a distribution.
//
// Required config properties:
//  - containerId
//  - dataAbbrev
//  - dataLabel
//  - parentId
//  - seriesData
//  - title
//  - xAxisUnits
//
window.distributionChartMarkup = function (config) {
    reportFunctionUsage("distributionChartMarkup");
    window.addMarkup(
        config.parentId,
        config.title,
        config.containerId,
        config.seriesData,
        config.dataAbbrev,
        config.height,
        config.rank,
        config.hideNotes,
        config.showEdit,
        config,
    );

    var chartType = "spline";
    var stacking = null;
    var categories = null;

    var options = createOptions(
        chartType,
        config.containerId,
        config.seriesData,
        stacking,
        config.dataLabel,
        config.dataAbbrev,
        categories,
        config,
    );

    options.plotOptions.column = {
        grouping: false,
    };

    // Override the default datetime X axis
    options.xAxis.type = "linear";
    options.xAxis.plotLines = [];

    var numDistributions = config.seriesData.filter(function (series) {
        return series.dataType === "distribution";
    }).length;

    config.seriesData.forEach(function (data, index) {
        // Make distribution series semi-transparent if there are more than one
        if (data.dataType === "distribution" && numDistributions > 1 && data.color.indexOf("#") === 0) {
            options.series[index].color = hexToRgba(data.color, 0.8);
        }

        // If the first bucket of any distribution series is <10, force the X axis
        // to start at 0
        if (data.dataType === "distribution" && data.data[0].x < 10) {
            options.xAxis.min = 0;
        }

        // Figure out which annotation to add to the histogram
        let annotationLabel;
        let annotationValue;
        let stat = 50;

        if (data.statid) {
            stat = data.statid;
        } else if (typeof window.dropdown !== "undefined" && typeof window.dropdown.stat !== "undefined") {
            stat = window.dropdown.stat.id;
        }

        if (stat === "average" && typeof data.avg !== "undefined") {
            annotationLabel = "Average";
            annotationValue = data.avg;
        } else if (typeof data[`p${stat}`] !== "undefined") {
            annotationLabel = stat === 50 ? "Median" : `${stat}th percentile`;
            annotationValue = data[`p${stat}`];
        }

        // Some queries may still only return the "median" stat; use this as a fallback
        if (typeof annotationValue === "undefined" && typeof data.median !== "undefined") {
            annotationLabel = "Median";
            annotationValue = data.median;
        }

        // Add median lines
        if (annotationLabel && annotationValue) {
            // if we have zones, use those to set the annotation color
            let annotationColor = data.color;
            if (typeof config.zones !== "undefined") {
                // set the default to the last zone
                annotationColor = config.zones[config.zones.length - 1].color;
                for (const zone of config.zones) {
                    if (annotationValue < zone.value) {
                        annotationColor = zone.color;
                        break;
                    }
                }
                // also set the data series color so the legend matches the zone
                data.color = annotationColor;
            }
            let labelText = `${annotationLabel} ${data.name}: ${annotationValue}${data.xAxisUnits}`;
            // if we have an annotation label set, use it
            if (typeof config.label !== "undefined") {
                labelText = `${config.label}: ${annotationValue}${data.xAxisUnits}`;
            }
            let label = {
                text: labelText,
                rotation: 0,
                style: {
                    color: annotationColor,
                    font: "12px 'Gotham', sans-serif",
                },
                x: 8,
                y: 20 + index * 24,
            };
            if (typeof config.medianLabel !== "undefined" && config.medianLabel === false) {
                label = {};
            }
            options.xAxis.plotLines.push({
                value: annotationValue,
                width: 2,
                dashStyle: "Dash",
                color: annotationColor,
                zIndex: 100,
                label: label,
            });
        }

        // turn on suffix for xAxis labels
        if (typeof data.xAxisUnits !== "undefined") {
            options.xAxis.labels.format = "{value}" + data.xAxisUnits;
        }
    });

    // Other display options
    options.plotOptions.series.lineWidth = 2;
    options.legend.enabled = typeof config.legend === "undefined" ? true : config.legend;
    if (typeof config.median !== "undefined" && config.median === false) {
        options.xAxis.plotLines = [];
    }
    if (typeof config.xAxis !== "undefined" && config.xAxis === false) {
        options.xAxis.labels.enabled = false;
    }
    if (typeof config.yAxis !== "undefined" && config.yAxis === false) {
        options.yAxis[0].visible = false;
    }
    if (typeof config.zones !== "undefined") {
        options.series[0].zoneAxis = "x";
        options.series[0].zones = config.zones;
    }
    return new Highcharts.Chart(options);
};

// One or more lines without any fill.
//   stacking - null, "normal" or "percent"
//   dataLabel - text to show in tooltip after data value, eg, "seconds" or "pages"
//   dataAbbrev - suffix for the stats
//
// optionsHash was introduced to manage new options without adding more function parameters. Available options are:
//     allowMultipleBudgets (bool | default: false) - Add a budget line for any series with a budget, rather than just the first series
//     allowMultipleYAxis (bool | default: false) - Add a separate Y axis for metrics that have non-time ranges
//
window.lineChartMarkup = function (
    parentId,
    title,
    containerId,
    seriesData,
    dataLabel,
    dataAbbrev,
    stacking,
    fillOpacity,
    notes,
    legend,
    height,
    rank,
    bHideNotes,
    showEdit,
    optionsHash,
) {
    reportFunctionUsage("lineChartMarkup");
    if (!optionsHash) {
        optionsHash = {};
    }

    optionsHash.type = "line";

    window.addMarkup(
        parentId,
        title,
        containerId,
        seriesData,
        dataAbbrev,
        height,
        rank,
        bHideNotes,
        showEdit,
        optionsHash,
    );

    return window.lineChart(
        containerId,
        seriesData,
        dataLabel,
        dataAbbrev,
        stacking,
        fillOpacity,
        notes,
        legend,
        height,
        optionsHash,
    );
};

// One or more lines without any fill.
//   stacking - null, "normal" or "percent"
//   dataLabel - text to show in tooltip after data value, eg, "seconds" or "pages"
window.lineChart = function (
    containerId,
    seriesData,
    dataLabel,
    dataAbbrev,
    stacking,
    fillOpacity,
    notes,
    legend,
    height,
    optionsHash,
) {
    reportFunctionUsage("lineChart");
    if (!optionsHash) {
        optionsHash = {};
    }

    if (optionsHash.allowMultipleYAxis) {
        // Figure out if any of the series should have a separate Y axis.
        var nextYAxis = 1;
        var yAxisMap = {};

        var getYAxisForMetric = function (metric) {
            if (yAxisMap[metric]) {
                return yAxisMap[metric];
            }

            yAxisMap[metric] = nextYAxis;
            nextYAxis++;

            return yAxisMap[metric];
        };

        seriesData.forEach(function (series) {
            if (series.name.indexOf("DOM Elements (Syn)") === 0 || series.name.indexOf("DOM Elements (LUX)") === 0) {
                // We know that DOM elements usually needs its own Y axis.
                series.yAxis = getYAxisForMetric("DOM Elements");
                series.yAxisTitle = "DOM Elements";
            } else if (series.name.indexOf("Total JS Errors") === 0) {
                // usually needs its own Y axis.
                series.yAxis = getYAxisForMetric("Total JS Errors");
            } else if (series.suffix === "KB") {
                // Size metrics should have a separate Y axis too
                series.yAxis = getYAxisForMetric("Size");
                series.yAxisTitle = "Size (KB)";
            } else if ("count" === series.metrictype) {
                series.yAxis = getYAxisForMetric("count");
                series.yAxisTitle = ""; // No title cuz used for both pvs and sessions
            } else if ("smallint" === series.metrictype || "requests" === series.metrictype) {
                series.yAxis = getYAxisForMetric("smallint");
                series.yAxisTitle = ""; // No title cuz used for general small ints
            } else if ("percent" === series.metrictype) {
                series.yAxis = getYAxisForMetric("percent");
                series.yAxisTitle = "Percent";
            } else if ("score_decimal" === series.metrictype) {
                series.yAxis = getYAxisForMetric("score_decimal");
                series.yAxisTitle = "";
            } else if ("ms" === series.suffix || "min" === series.suffix) {
                series.yAxis = getYAxisForMetric(series.suffix);
            } else if ("custom" === series.metrictype && "s" !== series.suffix) {
                // Put custom metrics on their own axis if they are not measured in seconds
                series.yAxis = getYAxisForMetric(series.metric);
                series.yAxisTitle = series.name;
            }
        });

        // It's possible that all series in the chart have been put on a
        // separate Y axis, which means there will be no left-hand Y axis!
        // Let's see if that's the case. First we get a list of all Y axes
        // being used in the chart:
        var allYAxis = seriesData
            .map(function (series) {
                if (typeof series.yAxis === "undefined") {
                    // By default most series will not have an explicit Y axis,
                    // which implies they are using the default ("0th") one.
                    return 0;
                }

                return series.yAxis;
            })
            .sort((a, b) => a - b);

        if (allYAxis.indexOf(0) === -1) {
            // The default (0th) axis isn't being used. We're going to fix
            // this by swapping one of the axes to be the default.
            var axisToSwap = allYAxis[allYAxis.length - 1]; // swap the LAST axis to the default

            seriesData.map(function (series) {
                if (series.yAxis === axisToSwap) {
                    // Restore this series to the default which is no explicit
                    // axis settings.
                    delete series.yAxis;
                    delete series.yAxisTitle;
                }
            });
        }
    }

    var options = createOptions(
        fillOpacity ? "areaspline" : "spline",
        containerId,
        seriesData,
        stacking,
        dataLabel,
        dataAbbrev,
        null,
        optionsHash,
    );

    // Sad - but can not figure out a way to set a yAxis max in code beforehand,
    // so set it here for any "percent" yAxis.
    for (let i = 0; i < options.yAxis.length; i++) {
        var yAxis = options.yAxis[i];
        if ("Percent" === yAxis.title.text || yAxis.labels.format === "{value} %") {
            yAxis.max = 0;
            for (let j = 0; j < options.series.length; j++) {
                yAxis.max = Math.max(yAxis.max, getYAxisPercentMax(options.series[j]));
            }
            yAxis.labels.format = "{value}%";
            yAxis.title.text = ""; // remove title in lieu of label
        }
    }

    // add plotlines
    addPlotLines(seriesData, options, notes, height, legend);

    // Add comparison plotline
    // The "compare" option should be an object with the following keys:
    //     split (Number) - The value on the x axis that the series should be split at
    //
    if (optionsHash.compare) {
        options.xAxis.plotLines.push({
            color: "#7d7d7d",
            value: optionsHash.compare.split,
            width: 2,
        });

        // Set a 48 hour tick interval
        options.xAxis.tickInterval = 48 * 60 * 60 * 1000;

        legend = false;

        if (optionsHash.hideAxes) {
            options.xAxis.visible = false;
            options.yAxis = options.yAxis.map(function (axis) {
                return Object.assign({}, axis, { visible: false });
            });
        }
    }

    // enable/disable  legend
    options.legend.enabled = legend;

    if (optionsHash.legendReverse) {
        options.legend.reversed = true;
    }

    // budget threshold and min
    if (typeof seriesData !== "undefined") {
        if (typeof seriesData[0] !== "undefined") {
            if (typeof seriesData[0].budget !== "undefined") {
                var reverse = false;
                if (typeof seriesData[0].budget_reverse !== "undefined") {
                    reverse = seriesData[0].budget_reverse;
                }
                if (!optionsHash.allowMultipleBudgets) {
                    addBudgetLine(options, seriesData, reverse);
                }
            }
            if ("undefined" !== typeof seriesData[0].min) {
                // If the first series has a "min" property, use that to set the min for the x-axis.
                options.xAxis.min = seriesData[0].min;
            }
        }
    }

    // Custom options for this type of chart.
    options.plotOptions.series.fillOpacity = fillOpacity;
    options.plotOptions.series.lineWidth = fillOpacity ? 2 : 2; // only show the line if we are NOT filling the area
    options.plotOptions.series.trackByArea = fillOpacity ? true : false;

    // Add budget lines to the chart
    if (optionsHash.allowMultipleBudgets) {
        var extraSeries = [];

        options.series.forEach(function (series) {
            if (typeof series.budget === "number") {
                var yAxisIndex = series.yAxis ? series.yAxis : 0;
                var overBudgetColor = "#e73b3b";
                var originalSeriesColor = series.color;

                // Add a threshold plotline on the Y axis for this budget
                options.yAxis[yAxisIndex].plotLines = [
                    {
                        value: series.budget,
                        width: 2,
                        color: overBudgetColor,
                        zIndex: 3,
                    },
                ];

                options.yAxis[yAxisIndex].minRange = series.budget;

                // Reverse the series colours so that values *over* the budget are red
                series.color = overBudgetColor;
                series.negativeColor = originalSeriesColor;
                series.legendColor = originalSeriesColor;
                series.threshold = series.budget;

                if (series.budget_reverse) {
                    series.color = originalSeriesColor;
                    series.negativeColor = overBudgetColor;
                }
            }
        });

        options.series.unshift.apply(options.series, extraSeries);
    }

    if (typeof optionsHash.zones !== "undefined") {
        options.series.forEach(function (series) {
            series.zoneAxis = "y";
            series.zones = optionsHash.zones;
        });
    }

    return new Highcharts.Chart(options);
};

window.sparklineChart = function (containerId, seriesData, optionsHash) {
    reportFunctionUsage("sparklineChart");
    if (!optionsHash) {
        optionsHash = {};
    }

    var height = 100;
    if (optionsHash.height) {
        height = optionsHash.height;
    }

    var lineColor = "#fff";
    if (optionsHash.lineColor) {
        lineColor = optionsHash.lineColor;
    }

    var lineWidth = 2;
    if (optionsHash.lineWidth) {
        lineWidth = optionsHash.lineWidth;
    }

    var options = {
        chart: {
            type: "spline",
            renderTo: containerId,
            backgroundColor: null,
            animation: true,
            borderWidth: 0,
            margin: [0, 0, 0, 0],
            style: {
                overflow: "visible",
            },
            height: height,
        },
        credits: {
            enabled: false,
        },
        exporting: {
            buttons: {
                contextButton: {
                    enabled: false,
                },
            },
        },
        legend: {
            enabled: false,
        },
        title: {
            text: null,
        },
        xAxis: {
            type: "datetime",
            labels: {
                enabled: false,
            },
            title: {
                text: null,
            },
            startOnTick: false,
            endOnTick: false,
            tickPositions: [],
            lineWidth: 0,
        },
        yAxis: {
            endOnTick: false,
            startOnTick: false,
            labels: {
                enabled: false,
            },
            title: {
                text: null,
            },
            tickPositions: [],
            lineWidth: 0,
        },
        tooltip: {
            enabled: false,
        },
        plotOptions: {
            series: {
                lineWidth: lineWidth,
                color: lineColor,
                turboThreshold: 0,
                borderWidth: 0,
                groupPadding: 0,
                animation: {
                    duration: 1000,
                },
                cursor: "pointer",
                marker: {
                    enabled: false,
                },
                states: {
                    hover: {
                        enabled: false,
                    },
                },
            },
        },
        series: seriesData,
    };

    return new Highcharts.Chart(options);
};

window.gaugeChart = function (containerId, seriesData, optionsHash) {
    reportFunctionUsage("gaugeChart");
    if (!optionsHash) {
        optionsHash = {};
    }

    var height = 170;
    if (optionsHash.height) {
        height = optionsHash.height;
    }

    var lineColor = "#fff";
    if (optionsHash.lineColor) {
        lineColor = optionsHash.lineColor;
    }

    var trackColor = "var(--subtle-emphasis)";
    if (optionsHash.trackColor) {
        trackColor = optionsHash.trackColor;
    }

    var borderColor = "var(--level_4)";
    if (optionsHash.borderColor) {
        borderColor = optionsHash.borderColor;
    }

    var options = {
        chart: {
            renderTo: containerId,
            backgroundColor: null,
            plotBackgroundColor: null,
            plotBorderWidth: null,
            plotShadow: false,
            type: "pie",
            height: height,
        },
        title: {
            text: seriesData === null ? "-" : seriesData,
            align: "center",
            verticalAlign: "middle",
            y: height / 2 - 45,
            style: {
                color: "var(--high-emphasis)",
                font: "40px 'Gotham', sans-serif",
                fontWeight: "800",
            },
        },
        exporting: {
            buttons: {
                contextButton: {
                    enabled: false,
                },
            },
        },
        credits: {
            enabled: false,
        },
        legend: {
            enabled: false,
        },
        tooltip: {
            enabled: false,
        },
        plotOptions: {
            pie: {
                innerSize: "90%",
                allowPointSelect: false,
                dataLabels: {
                    enabled: false,
                },
                borderWidth: 0,
                borderColor: borderColor,
                states: {
                    hover: {
                        enabled: false,
                    },
                },
            },
        },
        series: [{ colors: [lineColor, trackColor], data: [[seriesData], [100 - seriesData]] }],
    };

    return new Highcharts.Chart(options);
};

window.columnChartMarkup = function (
    parentId,
    title,
    containerId,
    seriesData,
    dataLabel,
    dataAbbrev,
    stacking,
    categories,
    optionsHash,
) {
    reportFunctionUsage("columnChartMarkup");
    if (!optionsHash) {
        optionsHash = {};
    }
    let height = 400;
    if (optionsHash.height) {
        height = optionsHash.height;
    }
    optionsHash.hideTrend = true;
    window.addMarkup(parentId, title, containerId, seriesData, dataAbbrev, height, false, true, false, optionsHash);
    return window.columnChart(containerId, seriesData, dataLabel, dataAbbrev, stacking, categories, optionsHash);
};

window.columnChart = function (containerId, seriesData, dataLabel, dataAbbrev, stacking, categories, optionsHash) {
    reportFunctionUsage("columnChart");
    var options = createOptions(
        "column",
        containerId,
        seriesData,
        stacking,
        dataLabel,
        dataAbbrev,
        categories,
        optionsHash,
    );
    return new Highcharts.Chart(options);
};

// Get back the same list of lists as we do for metriclist, but morph it into a table.
window.tableMarkup = function (parentId, title, containerId, seriesData, dataLabel, dataAbbrev, settingsData) {
    reportFunctionUsage("tableMarkup");
    window.addMarkup(parentId, title, containerId, seriesData, dataAbbrev, null, null, true, false, {
        hideDownload: true,
        hideTrend: true,
    });

    // Convert the JSON to a hash of hashes.
    var hTable = {}; // hash of page labels (or whatever) where the value is a hash of metrics
    var aMetricNames = [],
        aMetricSuffixs = [],
        aRowNames = [];
    var hMetricMin = {},
        hMetricMax = {};
    var hPercents = {};
    for (let d = 0; d < seriesData.length; d++) {
        var series = seriesData[d];
        const metricName = series.name.replace("Number", "#");
        aMetricNames.push(metricName);
        aMetricSuffixs.push(series.suffix);
        for (let i = 0; i < series.data.length; i++) {
            var data = series.data[i];
            const rowName = data.item;
            if (!hTable[rowName]) {
                hTable[rowName] = {};
            }
            if ("Page Views" === metricName) {
                // This metric includes all the pages, so we use it to get the percents and row order.
                aMetricNames.pop(); // remove it from the list of metrics
                aMetricSuffixs.pop(); // remove it from the list of metrics
                aRowNames.push(rowName);
                hPercents[rowName] = data.per;
                continue;
            }
            hTable[rowName][metricName] = data.y;

            // track min & max for color gradient
            hMetricMin[metricName] =
                "undefined" === typeof hMetricMin[metricName] ? data.y : Math.min(hMetricMin[metricName], data.y);
            hMetricMax[metricName] =
                "undefined" === typeof hMetricMax[metricName] ? data.y : Math.max(hMetricMax[metricName], data.y);
        }
    }

    var sListFilterName = settingsData.listfilter_name ? settingsData.listfilter_name : "&nbsp;";
    var sTable =
        "<table class=metrictable> <thead> <tr> <th style='width:200px;text-align: left'>" +
        sListFilterName +
        "</th> <th>% of Page Views</th>\n";
    // thead
    for (let j = 0; j < aMetricNames.length; j++) {
        const metricName = aMetricNames[j];
        sTable += "<th>" + metricName + "</th> ";
    }
    sTable += "</tr> </thead>\n";

    // tbody
    let totalRows = 0;
    for (let i = 0; i < aRowNames.length; i++) {
        const rowName = aRowNames[i];
        let rowPercent = hPercents[rowName];
        if (0 === rowPercent) {
            rowPercent = "< 1";
        }
        totalRows++;
        sTable +=
            "<tr class=hoverrow>" +
            "<td>" +
            "<div class='inline-help-content' style='display: block'>" +
            "<span class='inline-help-content__tool-tip-text'>" +
            rowName +
            "</span>" +
            "<div class=listfilter>" +
            '<a href="#setFilter" data-filter-value=' +
            JSON.stringify(rowName) +
            " onclick=\"setFilterUI('" +
            title +
            "', this.getAttribute('data-filter-value')); return false\" title=\"set filter\">" +
            rowName +
            "</a>" +
            "</div>" +
            "</div>" +
            "</td>" +
            "<td class=tdper>" +
            rowPercent +
            "%</td> ";
        var aValues = hTable[rowName];
        for (let j = 0; j < aMetricNames.length; j++) {
            const metricName = aMetricNames[j];
            const metricSuffix = aMetricSuffixs[j];
            var sColor = getPercentileColor(aValues[metricName], hMetricMin[metricName], hMetricMax[metricName]);
            sTable +=
                "<td class=tdnum style='background-color: " +
                sColor +
                ";'>" +
                ("undefined" === typeof aValues[metricName] || null === aValues[metricName]
                    ? "&ndash;"
                    : aValues[metricName] + metricSuffix) +
                "</td> ";
        }
        sTable += "</tr>\n";
    }
    sTable += "</table>\n";

    if (totalRows > 10) {
        sTable +=
            "<div class='metrictable__more'><button class='btn-more metrictable__more-btn'>Show More</button></div>";
    }

    var container = document.getElementById(containerId);
    container.innerHTML = sTable;
};

// A table of details.
var gaMonths = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
window.detailTableMarkup = function (parentId, title, containerId, seriesData, dataLabel, dataAbbrev) {
    reportFunctionUsage("detailTableMarkup");
    window.addMarkup(parentId, title, containerId, seriesData, dataAbbrev, null, null, true, false, {
        hideDownload: true,
        hideTrend: true,
    });

    const container = document.getElementById(containerId);
    const series = seriesData[0]; // assume only one series

    if (!series || series.data.length === 0) {
        container.innerText = noDataMessage;
        return;
    }

    var sTable = "<table class='detailtable table table-striped'>\n";
    var maxRows = 100;
    for (let i = 0; i < series.data.length && i < maxRows; i++) {
        var datum = series.data[i];
        sTable += "  <tr> <td class=detailtd>";
        if ("jserror_details" === containerId) {
            var d = new Date(1000 * datum.epoch);
            var sDate =
                ("0" + d.getHours()).substr(-2) +
                ":" +
                ("0" + d.getMinutes()).substr(-2) +
                ":" +
                ("0" + d.getSeconds()).substr(-2) +
                " " +
                gaMonths[d.getMonth()] +
                " " +
                d.getDate();
            sTable +=
                "<div class='detailitem'><p class='detailitem--wrap'>" +
                "<b>" +
                datum.message +
                "</b><br>" +
                sDate +
                ", <img src='//images.speedcurve.com/browsers/" +
                datum.browser.toString().toLowerCase().replace(" ", "_") +
                ".png?auto=format,compress' style='height: 20px;margin-top: -3px;'> " +
                datum.pbrowser +
                "<br>Domain: " +
                datum.hostname +
                "<br>Path: " +
                datum.pathname +
                "<br>" +
                "Page label: <a href='" +
                datum.url +
                "'>" +
                datum.pagelabel +
                "</a><br>" +
                (datum.filename && datum.filename !== datum.url
                    ? "Script: <a href='" + datum.filename + "'>" + datum.filename
                    : "HTML document") +
                "</a>, Line: " +
                datum.lineno +
                ", Column: " +
                datum.colno +
                "<br>" +
                "</p></div>";
        } else {
            // generic output
            var aKeys = Object.keys(datum); // assumes keys are in the order we want to present the data fields
            for (let k = 0; k < aKeys.length; k++) {
                var key = aKeys[k];
                if ("item" === key) {
                    continue; // skip "item"
                }
                var prettyKey = key.replace("__", " ").replace("url", "URL");
                prettyKey = prettyKey.charAt(0).toUpperCase() + prettyKey.slice(1);
                sTable += "<div><b>" + prettyKey + ":</b> " + datum[key] + "</div>";
            }
        }
        sTable += "</td> </tr>\n";
    }
    sTable += "</table>\n";

    container.innerHTML = sTable;
};

var gaPercentileColors = [
    "#a1dab4",
    "#8ed3b4",
    "#7bccb5",
    "#68c5b6",
    "#55beb8",
    "#41b6ba",
    "#2caebc",
    "#13a5bd",
    "#009cbe",
    "#0093bd",
    "#008abb",
];

function getPercentileColor(value, min, max) {
    reportFunctionUsage("getPercentileColor");
    var percentile = 50;
    if ("undefined" !== typeof value && min !== max) {
        percentile = Math.round((100 * (value - min)) / (max - min));
    }

    var iColor = Math.round(percentile / 10);

    return gaPercentileColors[iColor];
}

// Assumes that the data is a list of hashes with a property called "item" to be displayed.
window.listMarkup = function (
    parentId,
    title,
    containerId,
    seriesData,
    dataLabel,
    dataAbbrev,
    settingsData,
    sInSettings,
    sNotInSettings,
    bHorizontalBar,
    optionsHash,
) {
    reportFunctionUsage("listMarkup");
    window.addMarkup(parentId, title, containerId, seriesData, dataAbbrev, null, null, true, false, optionsHash);

    var aSettings = [];
    if (settingsData) {
        // Convert from Highcharts format to a normal array.
        for (let i = 0; i < settingsData.data.length; i++) {
            aSettings.push(settingsData.data[i]["item"]);
        }
    }

    var sLists = "";
    if (0 === seriesData.length) {
        sLists = noDataMessage;
    }
    var bAtLeastOneInSettings = false;
    for (let d = 0; d < seriesData.length; d++) {
        var series = seriesData[d];
        var sBarColor = series.color;
        sLists += "<div" + (d > 0 ? " style='margin-top: 1em;'" : "") + ">" + series.name + "</div>\n"; // add space between multiple lists in one chartcontainer
        var data = series.data;
        if (bHorizontalBar) {
            sLists +=
                "<div id='ol_" +
                containerId +
                "' class='js-list' style='display: grid; grid-template-columns: 34px minmax(33%, 50%) 1fr;'>";
        } else {
            sLists += "<ol id='ol_" + containerId + "'>";
        }
        for (let i = 0, len = data.length; i < len; i++) {
            var datum = data[i];
            var item = datum["item"];
            var aExtra = [];
            if ("undefined" !== typeof datum["num"]) {
                aExtra.push(datum["num"].toLocaleString() + " " + dataAbbrev);
            }
            if ("undefined" !== typeof datum["per"]) {
                aExtra.push(datum["per"] + "%");
            }
            var bInSettings = -1 !== aSettings.indexOf(item);
            bAtLeastOneInSettings = bAtLeastOneInSettings || bInSettings;
            if (bHorizontalBar) {
                sLists += '<div style="text-align: right; padding-right: 6px">' + (i + 1) + ".</div>";
                sLists +=
                    '<div class="js-list-label" style="margin-bottom: 0.1em; padding-right: 3em; display: flex;">';
                sLists += '<div style="word-break: break-all">';
            } else {
                sLists += '<li style="margin-bottom: 0.1em;">';
            }

            var itemNode = document.createElement("span");
            itemNode.textContent = item;
            itemNode.innerHTML += bInSettings ? "<sup>*</sup>" : "";
            var newItem = itemNode.outerHTML;
            if (
                "Top Page Labels" === series.name ||
                "Top Browsers" === series.name ||
                "Top Paths" === series.name ||
                "Top Countries" === series.name ||
                "Top Errors" === series.name ||
                "Device Types" === series.name ||
                "Effective Connection Type" === series.name
            ) {
                newItem =
                    '<a href="#setFilter" data-filter-value=' +
                    JSON.stringify(item) +
                    " onclick=\"setFilterUI('" +
                    series.name +
                    "', this.getAttribute('data-filter-value')); return false\" title=\"set filter\">" +
                    newItem +
                    "</a>";
            }
            sLists += newItem;

            var sExtra = aExtra.length ? " (" + aExtra.join(", ") + ")" : "";

            if (bHorizontalBar) {
                sLists += "</div>";
                sLists += '<div style="padding-left: 0.5em; white-space: nowrap;">' + sExtra + "</div>";
                sLists += "</div>";
                sLists += '<div aria-hidden="true" title="' + datum["per"] + '%" style="margin-bottom:2px">';

                var percentage = typeof datum.per !== "undefined" && datum.per > 0 ? datum.per : 0;
                var iBarWidth = Math.min(100, percentage);

                sLists +=
                    '	<div class="hidden-xs" style="background-color: ' +
                    sBarColor +
                    "; width: calc(" +
                    iBarWidth +
                    '% + 1px); height:100%;">&nbsp;</div>';
                sLists += "</div>";
            } else {
                sLists += sExtra + "</li>";
            }
        }
        if (bHorizontalBar) {
            sLists += "</div>";
        } else {
            sLists += "</ol>";
        }
    }
    if (bAtLeastOneInSettings) {
        sLists +=
            "<div style='color:#B3B3FF'>* " +
            (sInSettings ? sInSettings : "This value is in your Settings.") +
            "</div>\n";
    } else if (sNotInSettings) {
        sLists += "<div style='color:#B3B3FF'>" + sNotInSettings + "</div>\n";
    }

    var container = document.getElementById(containerId);
    container.innerHTML = sLists;

    if (bHorizontalBar) {
        // To get all of the bars on one page lined up, go through and make every label
        // the same width as the longest label.
        var lists = [].slice.apply(document.querySelectorAll(".js-list"));
        var labels = lists
            .map(function (list) {
                // All labels in a list are the same width; only need to deal with the first
                return list.querySelector(".js-list-label");
            })
            .filter(function (node) {
                return node;
            });

        var longest = labels
            .map(function (label) {
                return label.getBoundingClientRect().width;
            })
            .sort()
            .pop();

        labels.forEach(function (label) {
            label.style.minWidth = longest + "px";
        });
    }
};

window.columnChart = function (containerId, seriesData, dataLabel, dataAbbrev, stacking, categories, optionsHash) {
    reportFunctionUsage("columnChart");
    var options = createOptions(
        "column",
        containerId,
        seriesData,
        stacking,
        dataLabel,
        dataAbbrev,
        categories,
        optionsHash,
    );

    return new Highcharts.Chart(options);
};

window.trafficBudgetChart = function (settings, data) {
    reportFunctionUsage("trafficBudgetChart");
    let series = {};
    const container = settings.chart_id;
    let value;
    let value_display;
    let suffix = "";

    if (typeof data[0] !== "undefined") {
        series = data[0];
    }

    if (typeof series.median !== "undefined") {
        value = series.median;
        value_display = value;
    }

    if (typeof series.xAxisUnits !== "undefined") {
        suffix = series.xAxisUnits;
    }

    if (typeof series.statid !== "undefined") {
        if (typeof series[`p${series.statid}`] !== "undefined") {
            value = series[`p${series.statid}`];
            value_display = value;
        }
    }

    const threshold_1 = settings.traffic_budget_threshold_1;
    let threshold_1_display = settings.traffic_budget_threshold_1;
    const threshold_2 = settings.traffic_budget_threshold_2;
    let threshold_2_display = settings.traffic_budget_threshold_2;
    let color_class = "good";

    if (suffix === "s") {
        value = value * 1000;
        value_display = (value / 1000).toFixed(2);
        threshold_1_display = threshold_1 / 1000;
        threshold_2_display = threshold_2 / 1000;
    }

    if (typeof data[0] !== "undefined") {
        if (typeof data[0].metric !== "undefined") {
            if (data[0].metric === "dcls") {
                value_display = value.toFixed(2);
            }
        }
    }

    $("#" + container).html(
        "<div class='traffic-budget'>" +
            "<div class='traffic-budget__metric'><div class='traffic-budget__value'></div><div class='traffic-budget__arrow'></div></div>" +
            "<div class='traffic-budget__good'></div>" +
            "<div class='traffic-budget__improve'></div>" +
            "<div class='traffic-budget__poor'></div>" +
            "<div class='traffic-budget__threshold-1'>" +
            threshold_1_display +
            suffix +
            "</div>" +
            "<div class='traffic-budget__threshold-2'>" +
            threshold_2_display +
            suffix +
            "</div>" +
            "</div>",
    );

    if (value >= threshold_2) {
        color_class = "poor";
    } else if (value >= threshold_1) {
        color_class = "improve";
    }
    // even though we show the traffic light as 1/3 the thresholds might not be in thirds
    // base the max width on the distance between thresholds
    const max_width = threshold_2 + (threshold_2 - threshold_1);
    // work out which third we're in first
    let percentage = 50;
    if (value >= max_width) {
        percentage = 100;
    } else if (value >= threshold_2) {
        const chunk = max_width - threshold_2;
        const chunk_value = value - threshold_2;
        const chunk_percent = chunk_value / chunk;
        percentage = 66 + 33 * chunk_percent;
    } else if (value >= threshold_1) {
        const chunk = threshold_2 - threshold_1;
        const chunk_value = value - threshold_1;
        const chunk_percent = chunk_value / chunk;
        percentage = 33 + 33 * chunk_percent;
    } else if (value < threshold_1) {
        const chunk_percent = value / threshold_1;
        percentage = 33 * chunk_percent;
    }
    if (percentage > 90) {
        $("#" + container + " .traffic-budget__metric").css("display", "grid");
        $("#" + container + " .traffic-budget__value").css("justify-self", "end");
        $("#" + container + " .traffic-budget__arrow")
            .css("justify-self", "end")
            .css("marginRight", "3px");
    } else if (percentage < 10) {
        $("#" + container + " .traffic-budget__arrow").css("marginLeft", "3px");
    } else {
        $("#" + container + " .traffic-budget__value").addClass("traffic-budget__metric--center");
        $("#" + container + " .traffic-budget__arrow").addClass("traffic-budget__metric--center");
        $("#" + container + " .traffic-budget__metric").css("margin-left", percentage + "%");
    }
    if (typeof value_display !== "undefined") {
        $("#" + container + " .traffic-budget__value").html(value_display + suffix);
        $("#" + container + " .traffic-budget__arrow").addClass("traffic-budget__arrow--" + color_class);
        $("#" + container + " .traffic-budget__metric")
            .addClass("traffic-budget__color--" + color_class)
            .css("opacity", 1);
    }
    window.stopChartBusy(container);
};

// Return standard options.
function createOptions(chartType, containerId, seriesData, stacking, dataLabel, dataAbbrev, categories, optionsHash) {
    reportFunctionUsage("createOptions");
    if (!optionsHash) {
        optionsHash = {};
    }

    // stuff the function params into each series so that they are accessible by all functions
    if (seriesData) {
        seriesData.forEach(function (series) {
            series.speedcurveOptions = [];
            series.speedcurveOptions.dataLabel = dataLabel;
            series.speedcurveOptions.dataAbbrev = dataAbbrev;
            series.speedcurveOptions.stacking = stacking;
        });
    }

    // check if in compare mode
    var compare = false;
    if (
        !optionsHash.disableCompare &&
        typeof window.dropdown !== "undefined" &&
        typeof window.dropdown.dc.id !== "undefined"
    ) {
        if (window.dropdown.dc.id === 1) {
            // check that we only have line charts
            if (optionsHash.type && optionsHash.type === "line") {
                compare = true;
            }
        }
    }

    // check for categories and set the right x axis type
    var xType = "datetime";
    var borderWidth = 0;
    if (categories) {
        xType = "category";
        borderWidth = 1;
    }
    let groupPadding = 0;
    if (optionsHash.group_padding) {
        groupPadding = optionsHash.group_padding;
    }
    if (optionsHash.borderWidth) {
        borderWidth = optionsHash.borderWidth;
    }

    //Check for a optionsHash override.
    if (optionsHash.xType) {
        xType = optionsHash.xType;
    }

    //Check to see if X-Axis should be hidden.
    let xAxisVisible = true;
    let marginBottom = undefined;
    if (optionsHash.hideXAxis) {
        xAxisVisible = false;
        marginBottom = 5;
    }

    //Set any custom legend margins.
    let legendMargin = 0;
    if (optionsHash.legendMargin) {
        legendMargin = optionsHash.legendMargin;
    }

    // Turn on markers for series with only one data point.
    if ("undefined" !== typeof seriesData) {
        for (let i = 0; i < seriesData.length; i++) {
            const series = seriesData[i];
            if ("undefined" !== typeof series.data && series.data.length === 1) {
                series.marker = { enabled: true, symbol: "circle" };
            }
            // enable regression/trend lines
            if (window.bTrendsVisible) {
                if (chartType === "spline" || chartType === "areaspline") {
                    var showRegression = true;
                    // check for series.type used in histograms
                    if ("undefined" !== typeof series.type && series.type === "column") {
                        showRegression = false;
                    }
                    if (showRegression && series.data.length > 1) {
                        series.regression = true;
                        series.regressionSettings = {
                            name: series.name + " Trend",
                            color: new Highcharts.Color(series.color).setOpacity(1).get(),
                            lineWidth: 1,
                            dashStyle: "LongDash",
                            regressionSeriesOptions: {
                                suffix: series.suffix,
                            },
                        };
                    }
                }
            }
        }
    }

    var cursor = "pointer";

    let legend = true;
    if (typeof optionsHash.legend !== "undefined") {
        legend = optionsHash.legend;
    }

    let inverted = false;
    if (typeof optionsHash.inverted !== "undefined") {
        inverted = optionsHash.inverted;
    }

    const animation = typeof optionsHash.animation !== "undefined" ? optionsHash.animation : true;

    var options = {
        chart: {
            type: chartType,
            inverted: inverted,
            renderTo: containerId,
            backgroundColor: null,
            animation: animation,
            zoomType: "xy",
            spacingLeft: 3,
            spacingRight: 3,
            spacingBottom: 0,
            marginBottom: marginBottom,
            resetZoomButton: {
                theme: {
                    verticalAlign: "bottom",
                    padding: 10,
                    fill: "#282828",
                    stroke: "#7d7d7d",
                    style: {
                        color: "#7d7d7d",
                    },
                    r: 2,
                    states: {
                        hover: {
                            fill: "#ffffff",
                            style: {
                                color: "#282828",
                            },
                        },
                    },
                },
            },
        },
        exporting: {
            buttons: {
                contextButton: {
                    enabled: false,
                },
            },
            sourceWidth: 1400,
            sourceHeight: 600,
            fallbackToExportServer: false,
        },
        credits: {
            enabled: false,
        },
        legend: {
            enabled: legend,
            itemMarginTop: legendMargin,
            useHTML: true,
            labelFormatter: function () {
                const metaKey = navigator.userAgent.indexOf("Mac") !== -1 ? "⌘" : "Ctrl";

                return `<span title="${metaKey}+click to toggle all other series">${this.name}</span>`;
            },
            itemStyle: {
                color: "var(--medium-emphasis)",
                font: "12px 'Gotham', sans-serif",
            },
            itemHoverStyle: {
                color: "#ffffff",
            },
            align: "center",
        },
        title: {
            text: null,
        },
        xAxis: {
            type: xType,
            dateTimeLabelFormats: {
                day: "%e %b",
                month: "%b %y",
            },
            labels: {
                enabled: xAxisVisible,
                style: {
                    color: "var(--medium-emphasis)",
                    font: "12px 'Gotham', sans-serif",
                },
            },
            title: {
                text: null,
            },
            tickWidth: 0,
            lineWidth: 0,
            categories: categories,
        },
        yAxis: [gimmeSomeYAxis(dataAbbrev)],
        tooltip: {
            snap: 0,
            enabled: false,
            animation: false,
            hideDelay: 0,
            backgroundColor: "rgba(255, 255, 255, 1)",
            shadow: false,
            style: {
                fontFamily: "sans-serif",
                padding: 10,
                pointerEvents: "auto",
            },
            useHTML: true,
        },
        plotOptions: {
            series: {
                // Disable Highcharts default of 1000 data points for series that contain complex tuples.
                // See http://www.highcharts.com/errors/12
                turboThreshold: 0,
                borderWidth: borderWidth,
                borderColor: "var(--level_4)",
                connectNulls: true,
                groupPadding: groupPadding,
                stacking: stacking,
                cursor: cursor,
                marker: {
                    enabled: false,
                    symbol: "circle",
                },
                states: {
                    hover: {
                        lineWidth: 0,
                        lineWidthPlus: 0,
                    },
                    inactive: {
                        opacity: 0.4,
                    },
                },
                fillOpacity: 0.5,
                lineWidth: 0,
                suffix: "",
                point: {
                    events: {
                        click: function (e) {
                            //this.series.chart.tooltip.hide();
                            window.chartPop(
                                e.pageX,
                                e.pageY,
                                this.test,
                                Highcharts.dateFormat("%a %e %b", this.x, true),
                                Highcharts.dateFormat("%H:%M " + window.timezoneAbbr, this.x, true),
                                this.x,
                                this,
                                e,
                                containerId,
                                chartType,
                            );
                        },
                    },
                },
                events: {
                    legendItemClick: function (event) {
                        if (event.browserEvent.ctrlKey || event.browserEvent.metaKey) {
                            // When the user is holding the Ctrl or Cmd key, we treat this as a
                            // "toggle all other series" action.
                            const thisSeriesIndex = this.index;
                            const onlyThisVisible = this.chart.series.every(
                                (series) => !series.visible || series.index === thisSeriesIndex,
                            );

                            this.chart.series.forEach((series) => {
                                if (series.index !== thisSeriesIndex) {
                                    if (onlyThisVisible) {
                                        // When this series is the only one visible, we toggle the
                                        // other series to be visible again.
                                        series.show();
                                    } else {
                                        // Otherwise we toggle the other series so that only this
                                        // one is visible.
                                        series.hide();
                                    }
                                }
                            });

                            // Always keep the current series visible when a modifier key is held.
                            this.show();

                            return false;
                        }
                    },
                },
            },
        },
        series: seriesData,
    };

    if (animation) {
        options.plotOptions.series.animation = {
            duration: optionsHash.animationDuration || 1000,
        };
    } else {
        options.plotOptions.series.animation = false;
    }

    if (optionsHash.xAxisMin) {
        options.xAxis.min = optionsHash.xAxisMin;
    }

    if (optionsHash.xAxisMax) {
        options.xAxis.max = optionsHash.xAxisMax;
    }

    // Handle multiple y-axes.
    var aYAxes = []; // array of which series use which y-axis
    for (let i = 0; i < seriesData.length; i++) {
        const series = seriesData[i];
        const iY = series.yAxis ? series.yAxis : 0; // the number of this y-axis
        if (!aYAxes[iY]) {
            aYAxes[iY] = [];
        }
        aYAxes[iY].push(i);
    }

    // Create the y-axes
    for (let iY = 0; iY < aYAxes.length; iY++) {
        // A "0" y-axis exists by default, so we only need to create 1+:
        if (0 < iY) {
            const newY = gimmeSomeYAxis();
            options.yAxis.push(newY);
        }
        // Now - we HAVE to manipulate the y-axis already in options.
        options.yAxis[iY].opposite = 0 === iY ? false : true;
        const series = seriesData[aYAxes[iY][0]];
        if (aYAxes.length > 1) {
            options.yAxis[iY].labels.style.color = aYAxes[iY].length > 1 ? "var(--high-emphasis)" : series.color;
        }
        if (series.suffix) {
            var suffix = series.suffix;
            if ("s" !== suffix) {
                suffix = " " + suffix;
            }
            if (undefined === suffix) {
                suffix = "";
            }
            options.yAxis[iY].labels.format = "{value}" + suffix;
        }
        if ("%" === series.suffix) {
            // It's possible there are multiple percent series. We want to set
            // the maximum Y axis value based on the largest series.
            var maxValues = seriesData.map(function (series) {
                return series.suffix === "%" ? getYAxisPercentMax(series) : 0;
            });

            options.yAxis[iY].max = Math.max.apply(null, maxValues);
        }

        if (series.yAxisTitle) {
            options.yAxis[iY].title = {
                text: series.yAxisTitle,
                style: {
                    color: series.color,
                },
            };
        }
    }

    // if we have a maxY option then set the max on the first y axis
    if (optionsHash.maxY) {
        options.yAxis[0].max = optionsHash.maxY;
    }

    if (typeof window.SpeedCurve !== "undefined" && typeof window.SpeedCurve.chartOptions !== "undefined") {
        // We allow chart options to be injected via a global object SpeedCurve.chartOptions.
        // This enables us to modify some of the options on-the-fly for example when
        // taking screenshots of charts.
        options = $.extend(true, {}, options, window.SpeedCurve.chartOptions);
    }

    if (compare) {
        // set a fixed left margin so chart aligns with stats above
        options.chart.marginLeft = 60;
        // add spacing to top of chart so two line labels don't get trimmed
        options.chart.spacingTop = 15;
    }

    return options;
}

// Return the maximum value for a y-axis that is percentages.
// Typically it is "100" unless the values are much smaller.
function getYAxisPercentMax(series) {
    reportFunctionUsage("getYAxisPercentMax");
    if (series.metrictype === "percent") {
        // Some percentages (conversion & abandonment rates) are typically quite low (3-7%) so having the
        // Y-axis scale to 100% doesn't look great. Instead, we set it to
        // 150% of the maximum rate.
        var values = series.data.map(function (d) {
            return d.x ? d.y : 0;
        });
        var maxRate = Math.max.apply(null, values);

        if (maxRate > 40) {
            // So close, just go for 100.
            return 100;
        }

        return Math.min(100, Math.max(5, Math.round(maxRate * 1.5)));
    }

    return null;
}

// return a default y-axis
function gimmeSomeYAxis(dataAbbrev) {
    reportFunctionUsage("gimmeSomeYAxis");
    var suffix = "";
    if (dataAbbrev) {
        if (dataAbbrev !== "s") {
            suffix = " " + dataAbbrev;
        } else {
            suffix = dataAbbrev;
        }
    }
    return {
        labels: {
            enabled: true,
            style: {
                color: "var(--medium-emphasis)",
                font: "12px 'Gotham', sans-serif",
            },
            format: "{value}" + suffix,
            formatter: function () {
                if (this.axis.dataMax >= 10000) {
                    return window.formatNumber(this.value, true);
                }
                return this.axis.defaultLabelFormatter.call(this);
            },
        },
        title: { text: null },
        tickColor: "#fff",
        gridLineColor: "var(--super-subtle-emphasis)",
        reversedStacks: false,
        min: 0,
    };
}

function getSeriesTotal(data) {
    reportFunctionUsage("getSeriesTotal");
    let total = 0;

    if (Array.isArray(data)) {
        data.forEach(function (d) {
            total += d.y;
        });
    }

    return total;
}

function getSeriesLatest(data) {
    reportFunctionUsage("getSeriesLatest");
    if (data.length === 0) {
        return "";
    }
    return data[data.length - 1].y;
}

function getSeriesMax(data) {
    reportFunctionUsage("getSeriesMax");
    if (data.length === 0) {
        return "";
    }
    return Math.max(...data.map((item) => item.y));
}

function getSeriesMin(data) {
    reportFunctionUsage("getSeriesMin");
    if (data.length === 0) {
        return "";
    }
    return Math.min(...data.map((item) => item.y));
}

function addPlotLines(seriesData, options, notes, height, legend) {
    reportFunctionUsage("addPlotLines");
    // adjust height if legend is turned on
    var h = height;
    if (legend) {
        h = height - 100;
    }
    // clear plot lines before adding new ones!
    options.xAxis.plotLines = [{}];
    var y = 20;
    for (const key in notes) {
        if (notes.hasOwnProperty(key)) {
            options.xAxis.plotLines.push({
                className: "chart__note-line",
                value: notes[key].time,
                width: 1,
                color: "#7F7F7F",
                zIndex: 100,
                label: {
                    className: "chart__note-label",
                    text: notes[key].name,
                    rotation: 0,
                    style: {
                        color: "#7F7F7F",
                        font: "12px 'Gotham', sans-serif",
                    },
                    align: "right",
                    x: -5,
                    y: y,
                },
                events: {
                    mouseover: function () {
                        this.svgElem.attr({ stroke: "rgba(255, 255, 255, 1)" });
                        this.label.element.style.fill = "#ffffff";
                    },
                    mouseout: function () {
                        this.svgElem.attr({ stroke: "rgba(255, 255, 255, 0.5)" });
                        this.label.element.style.fill = "#7f7f7f";
                    },
                },
            });
            y += 20;
            if (y > h) {
                y = 20;
            }
        }
    }
    // check if we need to add an extra line to show the time periods when comparing
    if (typeof seriesData.time_middle !== "undefined") {
        options.xAxis.plotLines.push({
            value: seriesData.time_middle,
            width: 5,
            color: "#333", // #333
            zIndex: 3,
        });
    }
}

function addBudgetLine(options, seriesData, reverse) {
    reportFunctionUsage("addBudgetLine");
    // check if there's only one day of data then turn on markers
    var markers = false;
    if (seriesData[0].data.length === 1) {
        markers = true;
    }
    var top_color = "#e73b3b";
    var bottom_color = seriesData[0].color;
    if (reverse) {
        top_color = seriesData[0].color;
        bottom_color = "#e73b3b";
    }

    options.series = [
        {
            type: "areaspline",
            name: seriesData[0].name,
            color: top_color,
            negativeColor: "transparent",
            threshold: seriesData[0].budget,
            zIndex: 2,
            //suffix: data.budgets[key].suffix,
            data: seriesData[0].data,
            marker: {
                enabled: markers,
                states: {
                    hover: {
                        enabled: true,
                        fillColor: seriesData[0].color,
                    },
                },
            },
        },
        {
            type: "areaspline",
            name: seriesData[0].name,
            color: bottom_color,
            zIndex: 1,
            //suffix: data.budgets[key].suffix,
            data: seriesData[0].data,
            marker: {
                enabled: markers,
                states: {
                    hover: {
                        enabled: false,
                    },
                },
            },
        },
    ];
    options.yAxis[0].plotLines = [{ value: seriesData[0].budget, width: 2, color: "#e73b3b" }];
    options.yAxis[0].minRange = seriesData[0].budget;
    options.plotOptions.series.stacking = null;
}

// show the tooltip when clicking on a highchart point and show "Add note" and "View Test"
window.chartPop = function (x, y, test, date, time, timestamp, hcobj, eventObj, containerId, chartType) {
    reportFunctionUsage("chartPop");
    if (!$("#chartpop")) {
        return;
    }

    const chartX = hcobj.series.chart.plotLeft;
    const chartY = hcobj.series.chart.plotTop;
    const container = document.getElementById(containerId);
    //line graph, point dimensions
    let pointPlotX = chartX + eventObj.point.plotX + $(container).offset().left;
    const pointPlotY = chartY + eventObj.point.plotY + $(container).offset().top;

    // for grouped columns we want to point of the x of the actual shape
    if (eventObj.point.shapeArgs && eventObj.point.shapeType && eventObj.point.shapeType === "rect") {
        pointPlotX =
            chartX + eventObj.point.shapeArgs.x + eventObj.point.shapeArgs.width / 2 + $(container).offset().left;
    }

    // check if we have a site_id to be able to add a note
    let site_id = 0;
    if (typeof hcobj.site_id !== "undefined") {
        site_id = hcobj.site_id;
    }

    let html = window.formatPopup(hcobj, chartType);

    //Work out if this chart pop up is on a time series graph. If it is then we should display the marker.
    if (eventObj.point.shapeArgs) {
        //Not a time series graph. Remove the marker.
        $("#chartpop").removeClass("chart-tooltip--marker");
    } else {
        //Time series graph. Add the marker.
        $("#chartpop").addClass("chart-tooltip--marker");
    }

    let actionsHtml = "";
    actionsHtml += '<div class="chart-tooltip__actions">';
    let actionsAvailable = false;

    if (typeof test !== "undefined") {
        if (window.waterfall_mode === "benchmark") {
            actionsAvailable = true;
            actionsHtml +=
                "<a href='" +
                window.accountUrl +
                "/test/" +
                test +
                "/?share=" +
                window.filmstrip_share +
                "'><i class='icon-eye'></i>View Test</a><br>";
        } else {
            actionsAvailable = true;
            actionsHtml +=
                "<a href='" + window.accountUrl + "/test/" + test + "/'><i class='icon-eye'></i>View Test</a><br>";
        }
        if (!window.beingShared) {
            if (window.waterfall_mode !== "benchmark") {
                actionsAvailable = true;
                actionsHtml +=
                    "<a href='' class='compare__link compare__action' data-wpt_id='" +
                    test +
                    "' data-action='add' data-type='link'><i class='icon-compare'></i>Compare Test</a><br>";
                actionsHtml += `<a href='${window.accountUrl}/bookmarks/?wpt_id=${test}' class='bookmark__link bookmark__action' data-action='add' data-type='link' data-bookmark-type='syn'><i class='icon-bookmark'></i>Bookmark Test</a><br>`;
            }
        }
    }

    const seriesOptions = eventObj.point.series.options;
    const isRumMetric = seriesOptions.metricQualified && seriesOptions.metricQualified.substring(0, 3) === "LUX";
    const isCorrelation = seriesOptions.dataType && seriesOptions.dataType === "correlation";

    // Add the view sessions action if the metric that's been clicked on is a LUX metric.
    if (isRumMetric && !isCorrelation) {
        let startDate = null;
        let endDate = null;
        let filterLower = null;
        let filterUpper;
        const metric = seriesOptions.metric;

        if (eventObj.point.series.xAxis.options.type === "datetime") {
            // If it's a timeseries chart get the start and end date of the bucket.
            endDate = eventObj.point.options.x;

            // Check if the series has an xInterval that we can use
            if (typeof seriesOptions.xInterval !== "undefined") {
                startDate = endDate - seriesOptions.xInterval * 1000;
            } else {
                // Otherwise fall back to calculating the interval based on the previous or next point
                const indexOfEndDate = eventObj.point.series.processedXData.findIndex((date) => date === endDate);
                if (indexOfEndDate > 0) {
                    //Grab the date that's one before the end date, that will be our start date.
                    startDate = eventObj.point.series.processedXData[indexOfEndDate - 1];
                } else if (indexOfEndDate === 0 && eventObj.point.series.processedXData.length > 1) {
                    //Our data set starts at the end date. We need to work out the bucket size by looking at the difference between two dates and taking that off our end date.
                    const dateOnePastEndDate = eventObj.point.series.processedXData[1];
                    const bucketSize = dateOnePastEndDate - endDate;
                    startDate = endDate - bucketSize;
                } else {
                    //TODO: We only have one date in the chart. We have no idea of working out the bucket size. What do we do in this situation?
                }
            }

            //Create the action for the tool tip.
            if (endDate && startDate) {
                actionsAvailable = true;
                // Include all of the current dropdown parameters for the sessions dashboard
                const dropdownParameters = getDataURL()
                    // Remove the leading "?" so that we can build our own query string
                    .replace(/^\?/, "")
                    // Remove the "ld", "lds", and "lde" parameters so that we can set them
                    .replace(/&?(ld|lds|lde)=[^&]+/g, "");

                actionsHtml += `<a class="view-sessions__action"  href="${
                    window.accountUrl
                }/lux-sessions/?metric=${metric}&ld=-1&lds=${startDate / 1000}&lde=${
                    endDate / 1000
                }&${dropdownParameters}"><i class="icon-eye"></i>View Sessions</a><br>`;
                actionsHtml += `<a href="${window.accountUrl}/bookmarks/?metric=${metric}&dmetric=pbrowser&ld=-1&lds=${
                    startDate / 1000
                }&lde=${
                    endDate / 1000
                }&${dropdownParameters}&" class="bookmark__link bookmark__action" data-action="add" data-type="link" data-bookmark-type='rum'><i class="icon-bookmark"></i>Bookmark Sessions</a><br>`;
                actionsHtml +=
                    "<a href='' class='compare__link compare__rum__action' data-dropdown-params='" +
                    dropdownParameters +
                    "' data-start-date='" +
                    startDate +
                    "' data-end-date='" +
                    endDate +
                    "' data-action='add' data-type='link'><i class='icon-compare'></i>Compare Sessions</a><br>";
            }
        } else if (eventObj.point.series.xAxis.options.type === "linear") {
            filterLower = eventObj.point.options.x;
            filterUpper = "";

            const dropdownParameters2 = getDataURL();

            // Check if the series has an xInterval that we can use
            if (typeof seriesOptions.xInterval !== "undefined") {
                filterUpper = (filterLower + seriesOptions.xInterval).toFixed(2);
            } else {
                //If it's a linear chart get the filter low and filter high values.
                filterLower = eventObj.point.options.x;
                //Can we now step forward one to find the upper filter.
                const indexOfFilterLower = eventObj.point.series.processedXData.findIndex(
                    (unit) => unit === filterLower,
                );
                if (indexOfFilterLower < eventObj.point.series.processedXData.length - 1) {
                    //Grab the filter option that's one after the filter lower, that will be our filter upper.
                    filterUpper = eventObj.point.series.processedXData[indexOfFilterLower + 1];
                } else if (
                    indexOfFilterLower === eventObj.point.series.processedXData.length - 1 &&
                    eventObj.point.series.processedXData.length > 1
                ) {
                    //Our filter set starts at the end of the chart range. We need to work out the bucket size by looking at the difference between two pieces of xdata and adding that onto the filter lower.
                    const filterOneBeforeFilterLower =
                        eventObj.point.series.processedXData[eventObj.point.series.processedXData.length - 2];
                    const bucketSize = filterLower - filterOneBeforeFilterLower;
                    filterUpper = (filterLower + bucketSize).toFixed(2);
                } else {
                    //TODO: We only have one xdata point in the chart. We have no idea of working out the bucket size. What do we do in this situation?
                }
            }

            const urlParams = getDataURL().split("&");
            let luxDateParams = "";
            let dateRange = undefined;
            let startDate = undefined;
            let endDate = undefined;
            //Check to see if the date was set using the syn params, if it was then convert to lux params.
            urlParams.forEach((param) => {
                const paramLabelVal = param.split("=");
                if (paramLabelVal[0] === "d") {
                    luxDateParams += "&ld=" + paramLabelVal[1];
                    dateRange = paramLabelVal[1];
                } else if (paramLabelVal[0] === "ds") {
                    luxDateParams += "&lds=" + paramLabelVal[1];
                    startDate = paramLabelVal[1];
                } else if (paramLabelVal[0] === "de") {
                    luxDateParams += "&lde=" + paramLabelVal[1];
                    endDate = paramLabelVal[1];
                }
            });

            //Create the action for the tool tip.
            actionsAvailable = true;
            actionsHtml += `<a href="${
                window.accountUrl
            }/lux-sessions/?metric=${metric}&fl=${filterLower}&fu=${filterUpper}${luxDateParams}&${getDataURL().replace(
                /^\?/,
                "",
            )}"><i class="icon-eye"></i>View Sessions</a><br>`;

            if (dateRange !== undefined) {
                //On a favorites dashboard so include the date params.
                actionsHtml +=
                    "<a href='' class='compare__link compare__rum__action' data-dropdown-params='" +
                    dropdownParameters2 +
                    "' data-filter-lower='" +
                    filterLower +
                    "' data-filter-upper='" +
                    filterUpper +
                    "' data-metric='LUX|" +
                    metric +
                    "' data-start-date='" +
                    startDate +
                    "' data-end-date='" +
                    endDate +
                    "'  data-date-range='" +
                    dateRange +
                    "' data-action='add' data-type='link'><i class='icon-compare'></i>Compare Sessions</a><br>";
            } else {
                //On a curated RUM dashboard date params inside the dropdownParmaters2.
                actionsHtml +=
                    "<a href='' class='compare__link compare__rum__action' data-dropdown-params='" +
                    dropdownParameters2 +
                    "' data-filter-lower='" +
                    filterLower +
                    "' data-filter-upper='" +
                    filterUpper +
                    "' data-metric='LUX|" +
                    metric +
                    "' data-action='add' data-type='link'><i class='icon-compare'></i>Compare Sessions</a><br>";
            }
        }
    }

    //Only show the add note to time series charts.
    if (
        !window.beingShared &&
        !xAxisHasCategories(hcobj.series.chart.xAxis[0]) &&
        hcobj.series.chart.xAxis[0].options.type !== "linear" &&
        chartType !== "column"
    ) {
        if (window.waterfall_mode !== "benchmark") {
            actionsAvailable = true;
            actionsHtml +=
                "<a href='" +
                window.accountUrl +
                "/settings/note/add/" +
                site_id +
                "/" +
                timestamp +
                "/'><i class='icon-pencil'></i>Add Note</a>";
        }
    }

    actionsHtml += "</div>";
    if (actionsAvailable) {
        html += actionsHtml;
    }

    // adjust position of the popup
    $("#chartpop-content")
        .html(html)
        .ready(function () {
            // check if popup will go off the viewport
            let chartpop_offset = 0;
            const viewport_width = $(window).width() - 30; // 20px breathing room
            const chartpop_width = $("#chartpop").width() / 2; // half the actual width so we know where the center is
            if (x + chartpop_width > viewport_width) {
                chartpop_offset = x + chartpop_width - viewport_width;
            }
            $("#chartpop").css({
                top: pointPlotY - $("#chartpop").height() - 10 - $("#chartpop-marker").height() / 2,
                left: pointPlotX - $("#chartpop").width() / 2 - chartpop_offset,
            });
            if (eventObj.point.shapeArgs && eventObj.point.shapeType && eventObj.point.shapeType === "rect") {
                //we've got a shape object so lets make the hover area the shape being displayed in the chart.
                //bar chart, bar dimensions.
                let barWidth = eventObj.point.shapeArgs.width;
                if (barWidth < 40) {
                    barWidth = 40;
                }
                const barHeight = eventObj.point.shapeArgs.height + 16;

                //position the bar.
                const barX = $("#chartpop").width() / 2 - barWidth / 2;
                const barY = $("#chartpop").height();

                $("#chartpop-hover").css({ width: barWidth, height: barHeight, left: barX, top: barY });
            } else {
                //Reset the hover height first.
                $("#chartpop-hover").css({ width: 0, height: 0, left: 0, top: 0 });
                //No shape object so user has clicked on a point on a line. Make hover area cover that point on the line and the tool tip.
                $("#chartpop-hover").css({ width: $("#chartpop").width() + 10, height: $("#chartpop").height() + 40 });
            }
            $("#chartpop-marker").css({
                top: $("#chartpop").height() + 9,
                left: $("#chartpop").width() / 2 - 8 + chartpop_offset,
            });
            $("#chartpop-center").css({ "background-color": hcobj.color });
            $("#chartpop").css("opacity", 1);
            $("#chartpop").css("display", "block");
        });
};

window.formatPopup = function (hcobj, chartType) {
    reportFunctionUsage("formatPopup");
    // Allow an individual data series to override the chart's dataAbbrev.
    let dataAbbrev = "s";
    if (
        typeof hcobj.series.userOptions.speedcurveOptions !== "undefined" &&
        hcobj.series.userOptions.speedcurveOptions.dataAbbrev !== "undefined"
    ) {
        dataAbbrev = hcobj.series.userOptions.speedcurveOptions.dataAbbrev;
    }

    var suffix = "undefined" === typeof hcobj.series.userOptions.suffix ? dataAbbrev : hcobj.series.userOptions.suffix;
    if (suffix === undefined) {
        suffix = "";
    }
    var suffixTrimmed = suffix.replace(/ /g, "");

    if ("linear" === hcobj.series.chart.xAxis[0].options.type) {
        var xAxisUnits =
            typeof hcobj.series.userOptions.xAxisUnits === "undefined" ? "s" : hcobj.series.userOptions.xAxisUnits;
        var yLabelTooltip =
            suffixTrimmed === "pvs" || suffixTrimmed === "sessions"
                ? window.formatNumber(hcobj.y, true, hcobj)
                : hcobj.y + suffix;

        return (
            '<div class="chart-tooltip__data"><div class="chart-tooltip__hero-label" style="color:' +
            hcobj.color +
            '">' +
            hcobj.series.name +
            "<br>" +
            hcobj.x +
            xAxisUnits +
            "</span></div>" +
            '<div class="chart-tooltip__hero-value" style="color:' +
            hcobj.color +
            '">' +
            yLabelTooltip +
            "</div></div>"
        );
    } else if (!xAxisHasCategories(hcobj.series.chart.xAxis[0])) {
        // format for datetime series
        var this_y = hcobj.y;
        if (suffix === "KB") {
            if (this_y > 1024) {
                this_y = this_y / 1024;
                this_y = this_y.toFixed(2);
                suffix = "MB";
            }
        }
        if (suffix === "ms") {
            this_y = window.formatNumber(this_y);
        }
        let contentHtml = '<div class="chart-tooltip__data"><div class="chart-tooltip__meta-data">';
        if (chartType !== "column") {
            contentHtml +=
                Highcharts.dateFormat("%a %e %b", hcobj.x, true) +
                ", " +
                Highcharts.dateFormat("%H:%M " + window.timezoneAbbr, hcobj.x, true) +
                ("undefined" !== typeof hcobj.num ? "<br>" + window.formatNumber(hcobj.num, true, hcobj) : "") +
                "<br>";
        }
        contentHtml +=
            '<span class="chart-tooltip__hero-label" style="color:' +
            hcobj.color +
            '">' +
            hcobj.series.name +
            "</span>" +
            "</div>" +
            '<div class="chart-tooltip__hero-value" style="color:' +
            hcobj.color +
            '">' +
            this_y +
            suffix +
            "</div></div>";
        return contentHtml;
    } else {
        // format for categories
        let this_y = hcobj.y;
        if (suffix === "KB") {
            if (this_y > 1024) {
                this_y = this_y / 1024;
                this_y = this_y.toFixed(2);
                suffix = "MB";
            }
        }
        return (
            '<div class="chart-tooltip__data"><div class="chart-tooltip__meta-data">' +
            hcobj.category +
            "<br>" +
            '<span class="chart-tooltip__hero-label" style="color:' +
            hcobj.color +
            '">' +
            hcobj.series.name +
            "</span>" +
            "</div>" +
            '<div class="chart-tooltip__hero-value" style="color:' +
            hcobj.color +
            '">' +
            this_y +
            suffix +
            "</div></div>"
        );
    }
};

var chartpopDiv = document.createElement("div");
chartpopDiv.id = "chartpop";
chartpopDiv.className = "chart-tooltip";
chartpopDiv.style.display = "none";
chartpopDiv.innerHTML =
    '<div><div id="chartpop-content"></div></div><div id="chartpop-hover"><div id="chartpop-marker"><div id="chartpop-center"></div></div>';

document.body.appendChild(chartpopDiv);

$("#chartpop").mouseleave(function () {
    $("#chartpop").fadeOut(400);
});
$("#chartpop-hover").click(function () {
    $("#chartpop").fadeOut(400);
});

window.bTrendsVisible = false;
window.summaryMetric = "latest";

if (window.has_ls) {
    const trendsVisible = localStorage.getItem("charts-trend-visible");
    const summaryLocal = localStorage.getItem("charts-summary-metric");

    if (trendsVisible !== null) {
        window.bTrendsVisible = Boolean(JSON.parse(trendsVisible));
    }

    if (summaryLocal !== null) {
        window.summaryMetric = JSON.parse(summaryLocal);
    }
}

const trendsToggle = document.getElementById("trends-toggle");
const summaryRadio = document.querySelectorAll(".dashboard__dropdown-summary");

if (trendsToggle) {
    trendsToggle.checked = window.bTrendsVisible;
}

window.updateSummary = function () {
    reportFunctionUsage("updateSummary");
    summaryRadio.forEach((summary) => {
        if (summary.getAttribute("data-value") === window.summaryMetric) {
            summary.classList.add("active");
        } else {
            summary.classList.remove("active");
        }
    });
};

if (summaryRadio) {
    window.updateSummary();
    summaryRadio.forEach((summary) => {
        summary.addEventListener("click", function click(event) {
            event.preventDefault();
            window.summaryMetric = this.getAttribute("data-value");
            if (window.has_ls) {
                localStorage.setItem("charts-summary-metric", JSON.stringify(window.summaryMetric));
            }
            window.updateSummary();
            location.reload();
        });
    });
}

window.toggleTrend = function () {
    reportFunctionUsage("toggleTrend");
    window.bTrendsVisible = !window.bTrendsVisible;
    document.getElementById("trends-toggle").checked = window.bTrendsVisible;

    if (window.has_ls) {
        localStorage.setItem("charts-trend-visible", JSON.stringify(window.bTrendsVisible));
    }
};

// Remove the content of all elements with the "content" classname.
window.clearContent = function () {
    reportFunctionUsage("clearContent");
    var aContents = document.getElementsByClassName("content");
    for (let i = 0, len = aContents.length; i < len; i++) {
        var content = aContents[i];
        content.innerHTML = "";
    }
};

// Sometimes we need to empty a chart wrapper, eg, when we HAD custom metrics
// but then we switch to a page that does NOT have custom metrics.
window.clearChart = function (containerId) {
    reportFunctionUsage("clearChart");
    var wrapperId = containerId + "_wrapper";
    var chartWrapper = document.getElementById(wrapperId);
    if (chartWrapper) {
        chartWrapper.innerHTML = "";
    }
};

window.showChartError = function (chartWrapper, errorMessage) {
    reportFunctionUsage("showChartError");
    if (typeof chartWrapper === "string") {
        chartWrapper = document.getElementById(chartWrapper + "_wrapper");
    }

    const chartError = document.createElement("div");
    chartError.className = "chart__error panel-warning";
    chartError.innerText = errorMessage;

    chartWrapper.querySelector(".chart__content").prepend(chartError);
};

// Add a loading gif to all charts.
window.makeChartsBusy = function () {
    reportFunctionUsage("makeChartsBusy");
    var aChartWrappers = document.getElementsByClassName("chartwrapper");
    for (let i = 0, len = aChartWrappers.length; i < len; i++) {
        window.makeChartBusy(aChartWrappers[i]);
    }
};

// Add a loading gif to a chart.
window.makeChartBusy = function (chartWrapper, loadingText) {
    reportFunctionUsage("makeChartBusy");
    if (typeof chartWrapper === "string") {
        chartWrapper = document.getElementById(chartWrapper + "_wrapper");
    }

    if (chartWrapper) {
        const errorWrapper = chartWrapper.querySelector(".chart__error");
        let loadingWrapper = chartWrapper.querySelector(".chart__loading");
        let loadingTextWrapper = chartWrapper.querySelector(".loading-text");

        if (errorWrapper) {
            errorWrapper.parentNode.removeChild(errorWrapper);
        }

        if (!loadingWrapper) {
            const chartContent = chartWrapper.querySelector(".chart__content");
            if (chartContent) {
                chartContent.style.opacity = "0.25";
            }

            const chartFilters = chartWrapper.querySelector(".chart__filters");
            if (chartFilters) {
                chartFilters.style.opacity = "0.25";
            }

            loadingWrapper = document.createElement("div");
            loadingWrapper.className = "chart__loading";
            const cdn = window.SPEEDCURVE_CONFIG ? window.SPEEDCURVE_CONFIG.imageCdn : "/";
            var img = document.createElement("img");
            img.src = `${cdn}img/loading.svg`;
            loadingWrapper.appendChild(img);
            chartWrapper.appendChild(loadingWrapper);
        }

        if (loadingText) {
            if (!loadingTextWrapper) {
                loadingTextWrapper = document.createElement("div");
                loadingTextWrapper.className = "loading-text";
                loadingWrapper.appendChild(loadingTextWrapper);
            }

            if (loadingTextWrapper.innerHTML !== loadingText) {
                loadingTextWrapper.innerHTML = loadingText;
            }
        }
    }
};

// Add a loading gif to a chart.
window.stopChartBusy = function (chartWrapper) {
    reportFunctionUsage("stopChartBusy");
    if (typeof chartWrapper === "string") {
        chartWrapper = document.getElementById(chartWrapper + "_wrapper");
    }
    if (chartWrapper) {
        const chartContent = chartWrapper.querySelector(".chart__content");
        if (chartContent) {
            chartContent.style.opacity = "1";
        }

        const chartFilters = chartWrapper.querySelector(".chart__filters");
        if (chartFilters) {
            chartFilters.style.opacity = "1";
        }

        const chartLoading = chartWrapper.querySelector(".chart__loading");
        if (chartLoading) {
            chartLoading.parentNode.removeChild(chartLoading);
        }
    }
};

// Format an INTEGER!!!
window.formatNumber = function (num, bMag, point) {
    reportFunctionUsage("formatNumber");
    num = parseInt(num); // make sure it is an integer
    if (bMag) {
        // change a number to K and M if necessary
        // Round if necessary - eg, 24.3K is good but 224.3K is bad, just do 224K.
        if (num >= 1000000) {
            num = (num / 1000000).toFixed(num >= 100000000 ? 0 : 1) + "M";
        } else if (num >= 1000) {
            num = (num / 1000).toFixed(num >= 100000 ? 0 : 1) + "K";
        }
        num = num + ""; // convert to string
        num = num.replace(".0", ""); // unnecessary to show .0
    } else {
        // add commas if necessary
        var aChars = ("" + num).split("");
        var newnum = "";
        var cntr = 0;
        for (let i = aChars.length - 1; i >= 0; i--) {
            newnum = aChars[i] + (cntr > 0 && 0 === cntr % 3 ? "," : "") + newnum;
            cntr++;
        }
        num = newnum;
    }

    var suffix = "";
    if (point && "string" === typeof point.series.chart.options.series[point.series.index].numlabel) {
        suffix = " " + point.series.chart.options.series[point.series.index].numlabel;
    }

    return num + suffix;
};

// Given two data series, return a new data series that is the
// percentage (rate) of the first series over the second.
window.getRate = function (data1, data2) {
    reportFunctionUsage("getRate");
    var aData = [];
    for (let i1 = 0, i2 = 0, len = data1.length; i1 < len; i1++, i2++) {
        var tuple1 = data1[i1];
        var tuple2 = data2[i2];
        if (!tuple2) {
            continue;
        }

        var tuple = {}; // new tuple containing rate
        tuple.x = tuple1.x;
        if (tuple1.num) {
            tuple.num = tuple1.num;
        }

        // We KNOW that data2 has an entry corresponding to every entry in data1,
        // but data2 might have extras. So we make sure to skip over any extras.
        while (tuple1.x !== tuple2.x) {
            i2++;
            if (i2 >= data2.length) {
                break;
            }
            tuple2 = data2[i2];
        }
        tuple.y = Math.round(100 * (tuple1.y / tuple2.y));
        aData.push(tuple);
    }

    return aData;
};

////// begin new chart objects

// Return the number of charts in the viewport.
window.numChartsInViewport = function () {
    reportFunctionUsage("numChartsInViewport");
    var numCharts = 0;
    for (let i = 0, aElems = document.getElementsByClassName("chartwrapper"); i < aElems.length; i++) {
        var elem = aElems[i];
        if (inViewport(elem)) {
            numCharts++;
        }
    }

    return numCharts;
};

// Return true if the element is in the viewport.
function inViewport(e) {
    reportFunctionUsage("inViewport");
    var vh = document.documentElement.clientHeight;
    var vw = document.documentElement.clientWidth;

    // Return true if the top-left corner is in the viewport and it has width & height.
    var lt = findPos(e);
    return lt[0] >= 0 && lt[1] >= 0 && lt[0] < vw && lt[1] < vh && e.offsetWidth > 0 && e.offsetHeight > 0;
}

// Return an array containing the top & left coordinates of the element.
// from http://www.quirksmode.org/js/findpos.html
function findPos(e) {
    reportFunctionUsage("findPos");
    let curleft = 0;
    let curtop = 0;

    while (e) {
        curleft += e.offsetLeft;
        curtop += e.offsetTop;
        e = e.offsetParent;
    }

    return [curleft, curtop];
}

function xAxisHasCategories(xAxis) {
    reportFunctionUsage("xAxisHasCategories");
    return typeof xAxis.categories !== "undefined" && xAxis.categories.length > 0;
}

// Find the order of a chart in the page. 1 is first, etc.
window.getChartNum = function (id) {
    reportFunctionUsage("getChartNum");
    id += "_wrapper";
    for (let i = 0, aElems = document.getElementsByClassName("chartwrapper"); i < aElems.length; i++) {
        var elem = aElems[i];
        if (elem.id === id) {
            return i + 1;
        }
    }

    return 0;
};

// Get the title from chart placeholder.
window.getTitleFromPlaceholder = function (chartid) {
    reportFunctionUsage("getTitleFromPlaceholder");
    var title = "";
    var wrapper = document.getElementById(chartid + "_wrapper");
    if (wrapper) {
        var aHeaders = wrapper.getElementsByClassName("main_header");
        if (aHeaders.length) {
            title = aHeaders[0].innerHTML;
        }
    }

    return title;
};

// hexToRgba('#32a1da', 0.6) => 'rgba(50, 161, 218, 0.6)'
function hexToRgba(hex, alpha) {
    reportFunctionUsage("hexToRgba");
    return (
        "rgba(" +
        [parseInt(hex.slice(1, 3), 16), parseInt(hex.slice(3, 5), 16), parseInt(hex.slice(5, 7), 16), alpha].join(
            ", ",
        ) +
        ")"
    );
}

////// end new chart objects

if (LUX) {
    LUX.measure("chartsjs eval", "chartsjs before");
}
